﻿import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Heading, Text, Input, Button, LoadingSpinner, SortableTableHeader, Editor, Tooltip as UiTooltip } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { modulesService } from '../services/modules'
import { shopService } from '../services/shop'
import { serverInfoService } from '../services/serverInfo'
import { userLogsService } from '../services/userLogs'
import {
  ShopProduct,
  ShopCategory,
  ShopOrder,
  ShopBroker,
  ShopParameter,
  ShopPointsHistory,
  ShopOrderStatus,
  ShopProductImage,
  ShopOrderUpdateData,
  ShopStockMovement,
  ShopStockMovementType,
  ShopHistoricoAcesso,
  ShopTopAcessoEntry,
} from '../types/shop'
import { ChevronLeftIcon, ChevronRightIcon, AdjustmentsHorizontalIcon, ArrowTopRightOnSquareIcon, BeakerIcon } from '@heroicons/react/24/outline'
import { useAuthContext } from '../contexts/AuthContext'
import { useLocation } from 'react-router-dom'
import { usePermission } from '../hooks/usePermissions'
import { supabase } from '../lib/supabase'
import { ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line } from 'recharts'
import {
  getDateInSPTimezone,
  enumerateDateRangeYmd,
  getStartDateForLastNDaysInclusive,
} from '../services/superapp'

const SHOP_PROD_ORIGIN = 'https://shop.portalpasi.com.br'
const SHOP_HOMOLOG_ORIGIN = 'http://10.60.60.115:3091'
const HISTORICO_CHART_MAX_DAYS = 90

type ShopHistoricoViewType = 'lista' | 'graficos'

// Componente para exibir imagem do produto com URL assinada
const ProductImageDisplay: React.FC<{ image: ShopProductImage }> = ({ image }) => {
  const [imageUrl, setImageUrl] = useState<string>(image.image_url)

  useEffect(() => {
    // Se a image_url parece ser um path (não uma URL completa), gerar URL assinada
    if (!image.image_url.startsWith('http')) {
      shopService.getProductImageUrl(image.image_url).then(url => {
        if (url) setImageUrl(url)
      }).catch(console.error)
    }
  }, [image.image_url])

  return (
    <img
      src={imageUrl}
      alt={image.alt_text || 'Produto'}
      className="w-full h-full object-cover"
      onError={() => {
        // Fallback para URL original se a assinada falhar
        setImageUrl(image.image_url)
      }}
    />
  )
}

type TabType = 'produtos' | 'estoque' | 'pedidos' | 'corretores' | 'indicadores' | 'historico' | 'parametros'
type ProductSubTabType = 'cadastro' | 'categorias'
type CorretoresSubTabType = 'pontuacao' | 'historico'
type IndicatorsRankingTabType = 'produtos' | 'corretores' | 'favoritos'
type PedidosViewTab = 'listagem' | 'kanban'

const PEDIDOS_KANBAN_COLUMNS: Array<{
  status: ShopOrderStatus
  label: string
  columnClass: string
  headerClass: string
  headerTitleClass: string
  bodyTintClass: string
}> = [
  {
    status: 'pending',
    label: 'Pendente',
    columnClass: 'border-l-4 border-l-yellow-500 border border-gray-200 dark:border-gray-700',
    headerClass: 'bg-yellow-100 dark:bg-yellow-900/35 border-b border-yellow-200/90 dark:border-yellow-800',
    headerTitleClass: 'text-yellow-950 dark:text-yellow-100',
    bodyTintClass: 'bg-yellow-50/70 dark:bg-yellow-950/20',
  },
  {
    status: 'confirmed',
    label: 'Confirmado',
    columnClass: 'border-l-4 border-l-primary-500 border border-gray-200 dark:border-gray-700',
    headerClass: 'bg-primary-100 dark:bg-primary-900/35 border-b border-primary-200/90 dark:border-primary-800',
    headerTitleClass: 'text-primary-950 dark:text-primary-100',
    bodyTintClass: 'bg-primary-50/70 dark:bg-primary-950/20',
  },
  {
    status: 'shipped',
    label: 'Enviado',
    columnClass: 'border-l-4 border-l-purple-500 border border-gray-200 dark:border-gray-700',
    headerClass: 'bg-purple-100 dark:bg-purple-900/35 border-b border-purple-200/90 dark:border-purple-800',
    headerTitleClass: 'text-purple-950 dark:text-purple-100',
    bodyTintClass: 'bg-purple-50/70 dark:bg-purple-950/20',
  },
  {
    status: 'delivered',
    label: 'Entregue',
    columnClass: 'border-l-4 border-l-green-500 border border-gray-200 dark:border-gray-700',
    headerClass: 'bg-green-100 dark:bg-green-900/35 border-b border-green-200/90 dark:border-green-800',
    headerTitleClass: 'text-green-950 dark:text-green-100',
    bodyTintClass: 'bg-green-50/70 dark:bg-green-950/20',
  },
]

function truncateKanbanBrokerLabel(name: string, maxChars: number): string {
  if (name.length <= maxChars) return name
  return `${name.slice(0, maxChars)}…`
}

function shopOrderSnapshotForActivityLog(order: ShopOrder): Record<string, unknown> {
  return {
    status: order.status,
    shipping_tracking_code: order.shipping_tracking_code ?? '',
    shipping_provider: order.shipping_provider ?? '',
    shipping_cost: order.shipping_cost ?? null,
    notes: order.notes ?? '',
    shipped_at: order.shipped_at ?? null,
    delivered_at: order.delivered_at ?? null,
    cancelled_at: order.cancelled_at ?? null,
    cancelled_by: order.cancelled_by ?? null,
    cancellation_reason: order.cancellation_reason ?? null,
  }
}

function mergedOrderSnapshotAfterUpdate(order: ShopOrder, patch: ShopOrderUpdateData): Record<string, unknown> {
  return {
    status: patch.status ?? order.status,
    shipping_tracking_code:
      patch.shipping_tracking_code !== undefined ? patch.shipping_tracking_code : order.shipping_tracking_code ?? '',
    shipping_provider:
      patch.shipping_provider !== undefined ? patch.shipping_provider : order.shipping_provider ?? '',
    shipping_cost:
      patch.shipping_cost !== undefined ? patch.shipping_cost : order.shipping_cost ?? null,
    notes: patch.notes !== undefined ? patch.notes : order.notes ?? '',
    shipped_at: patch.shipped_at !== undefined ? patch.shipped_at : order.shipped_at ?? null,
    delivered_at: patch.delivered_at !== undefined ? patch.delivered_at : order.delivered_at ?? null,
    cancelled_at: patch.cancelled_at !== undefined ? patch.cancelled_at : order.cancelled_at ?? null,
    cancelled_by: patch.cancelled_by !== undefined ? patch.cancelled_by : order.cancelled_by ?? null,
    cancellation_reason:
      patch.cancellation_reason !== undefined ? patch.cancellation_reason : order.cancellation_reason ?? null,
  }
}

const Shop: React.FC = () => {
  const location = useLocation()
  const { user } = useAuthContext()
  const { hasPermission: canInsertShop } = usePermission('portais_shop', 'insert')
  const { hasPermission: canUpdateShop } = usePermission('portais_shop', 'update')
  const [activeTab, setActiveTab] = useState<TabType>('pedidos')
  const [moduleTitle, setModuleTitle] = useState<string>('Shop')
  const [moduleDescription, setModuleDescription] = useState<string>('E-commerce de PASI Points para corretores')

  // Estados gerais
  const [loading, setLoading] = useState<boolean>(false)

  // Estados para aba Produtos
  const [products, setProducts] = useState<ShopProduct[]>([])
  const [loadingProducts, setLoadingProducts] = useState<boolean>(true)
  const [categories, setCategories] = useState<ShopCategory[]>([])
  const [productFilterCategory, setProductFilterCategory] = useState<string>('')
  const [productFilterActive, setProductFilterActive] = useState<string>('active')
  const [productFilterFeatured, setProductFilterFeatured] = useState<string>('all')
  const [productFilterName, setProductFilterName] = useState<string>('')
  const [isProductModalOpen, setIsProductModalOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Partial<ShopProduct> | null>(null)
  const [productSubTab, setProductSubTab] = useState<ProductSubTabType>('cadastro')

  // Estados para sub-aba Categorias (dentro de Produtos)
  const [loadingCategories, setLoadingCategories] = useState<boolean>(true)
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Partial<ShopCategory> | null>(null)
  const [deletingCategoryId, setDeletingCategoryId] = useState<string | null>(null)

  // Estados para aba Estoque
  const [stockMovements, setStockMovements] = useState<ShopStockMovement[]>([])
  const [loadingStockMovements, setLoadingStockMovements] = useState<boolean>(false)
  const [stockFilterProductId, setStockFilterProductId] = useState<string>('')
  const [stockFilterMovementType, setStockFilterMovementType] = useState<string>('all')
  const [stockDateFrom, setStockDateFrom] = useState<string>('')
  const [stockDateTo, setStockDateTo] = useState<string>('')
  const [isStockModalOpen, setIsStockModalOpen] = useState(false)
  const [stockModalProduct, setStockModalProduct] = useState<ShopProduct | null>(null)
  const [stockMovementType, setStockMovementType] = useState<ShopStockMovementType>('in')
  const [stockMovementQuantity, setStockMovementQuantity] = useState<number>(1)
  const [stockMovementDescription, setStockMovementDescription] = useState<string>('')
  const [savingStockMovement, setSavingStockMovement] = useState(false)
  const [stockSubTab, setStockSubTab] = useState<'produtos' | 'historico'>('produtos')
  const [stockProductsPage, setStockProductsPage] = useState(1)
  const [stockMovementsPage, setStockMovementsPage] = useState(1)

  // Estados para aba Pedidos
  const [orders, setOrders] = useState<ShopOrder[]>([])
  const [loadingOrders, setLoadingOrders] = useState<boolean>(true)
  const [orderFilterStatus, setOrderFilterStatus] = useState<string>('all')
  const [orderFilterScgpId, setOrderFilterScgpId] = useState<string>('')
  const [orderFilterBrokerName, setOrderFilterBrokerName] = useState<string>('')
  const [orderDateFrom, setOrderDateFrom] = useState<string>('')
  const [orderDateTo, setOrderDateTo] = useState<string>('')
  const [selectedOrder, setSelectedOrder] = useState<ShopOrder | null>(null)
  const [isOrderDetailModalOpen, setIsOrderDetailModalOpen] = useState(false)
  const [orderSortBy, setOrderSortBy] = useState<string>('created_at')
  const [orderSortOrder, setOrderSortOrder] = useState<'asc' | 'desc'>('desc')
  const [ordersPage, setOrdersPage] = useState(1)
  const [pedidosViewTab, setPedidosViewTab] = useState<PedidosViewTab>('listagem')

  // Estados para aba Corretores
  const [brokers, setBrokers] = useState<ShopBroker[]>([])
  const [loadingBrokers, setLoadingBrokers] = useState<boolean>(false)
  const [brokerFilterScgpId, setBrokerFilterScgpId] = useState<string>('')
  const [brokerFilterName, setBrokerFilterName] = useState<string>('')
  const [appliedBrokerFilters, setAppliedBrokerFilters] = useState({ scgpId: '', brokerName: '' })
  const [hasSearchedBrokers, setHasSearchedBrokers] = useState(false)
  const [brokerSortBy, setBrokerSortBy] = useState<string>('created_at')
  const [brokerSortOrder, setBrokerSortOrder] = useState<'asc' | 'desc'>('desc')
  const [selectedBrokerForModal, setSelectedBrokerForModal] = useState<ShopBroker | null>(null)
  const [isBrokerModalOpen, setIsBrokerModalOpen] = useState(false)
  const [brokerFavoriteProducts, setBrokerFavoriteProducts] = useState<Array<{ product_id: string; product_name: string }>>([])
  const [loadingBrokerFavorites, setLoadingBrokerFavorites] = useState(false)
  const [corretoresSubTab, setCorretoresSubTab] = useState<CorretoresSubTabType>('pontuacao')
  const [acessandoBrokerId, setAcessandoBrokerId] = useState<string | null>(null)
  const [homologandoBrokerId, setHomologandoBrokerId] = useState<string | null>(null)
  const [isPointsAdjustModalOpen, setIsPointsAdjustModalOpen] = useState(false)
  const [pointsAdjustBroker, setPointsAdjustBroker] = useState<ShopBroker | null>(null)
  const [pointsAdjustOperation, setPointsAdjustOperation] = useState<'add' | 'remove'>('add')
  const [pointsAdjustQuantity, setPointsAdjustQuantity] = useState<number>(1)
  const [pointsAdjustObservation, setPointsAdjustObservation] = useState('')
  const [savingPointsAdjust, setSavingPointsAdjust] = useState(false)

  // Estados para sub-aba Extrato (dentro de Corretores)
  const [pointsHistory, setPointsHistory] = useState<ShopPointsHistory[]>([])
  const [loadingPointsHistory, setLoadingPointsHistory] = useState<boolean>(false)
  const [historyFilterType, setHistoryFilterType] = useState<string>('all')
  const [historyFilterScgpId, setHistoryFilterScgpId] = useState<string>('')
  const [historyFilterBrokerName, setHistoryFilterBrokerName] = useState<string>('')
  const [historyDateFrom, setHistoryDateFrom] = useState<string>('')
  const [historyDateTo, setHistoryDateTo] = useState<string>('')
  const [appliedHistoryFilters, setAppliedHistoryFilters] = useState({
    type: 'all',
    scgpId: '',
    brokerName: '',
    dateFrom: '',
    dateTo: '',
  })
  const [hasSearchedHistory, setHasSearchedHistory] = useState(false)
  const [historySortBy, setHistorySortBy] = useState<string>('created_at')
  const [historySortOrder, setHistorySortOrder] = useState<'asc' | 'desc'>('desc')
  const [brokersPage, setBrokersPage] = useState(1)
  const [historyPage, setHistoryPage] = useState(1)

  // Estados para aba Histórico de Acessos do Shop
  const [shopHistoricoView, setShopHistoricoView] = useState<ShopHistoricoViewType>('lista')
  const [shopHistoricoAcessos, setShopHistoricoAcessos] = useState<ShopHistoricoAcesso[]>([])
  const [shopHistoricoAcessosPrimeiroAcesso, setShopHistoricoAcessosPrimeiroAcesso] = useState<ShopHistoricoAcesso[]>([])
  const [loadingShopHistorico, setLoadingShopHistorico] = useState<boolean>(false)
  const [shopTotalHistorico, setShopTotalHistorico] = useState<number>(0)
  const [shopAcessosHoje, setShopAcessosHoje] = useState<number>(0)
  const [shopCorretoresComAcesso, setShopCorretoresComAcesso] = useState<number>(0)
  const [shopTopCorretoresAcesso, setShopTopCorretoresAcesso] = useState<ShopTopAcessoEntry[]>([])
  const [shopCorretorQuery, setShopCorretorQuery] = useState('')
  const [shopDateFrom, setShopDateFrom] = useState('')
  const [shopDateTo, setShopDateTo] = useState('')
  const [shopSortBy, setShopSortBy] = useState<string>('accessed_at')
  const [shopSortOrder, setShopSortOrder] = useState<'asc' | 'desc'>('desc')
  const [shopCurrentPage, setShopCurrentPage] = useState(1)
  const SHOP_ITEMS_PER_PAGE = 20
  const shopTableScrollRef = useRef<HTMLDivElement | null>(null)

  // Estados para aba Parâmetros
  const [parameters, setParameters] = useState<ShopParameter[]>([])
  const [loadingParameters, setLoadingParameters] = useState<boolean>(true)
  const [editingParameter, setEditingParameter] = useState<ShopParameter | null>(null)
  const [isParameterModalOpen, setIsParameterModalOpen] = useState(false)
  const [parameterEditValue, setParameterEditValue] = useState<string>('')
  const [savingParameter, setSavingParameter] = useState(false)
  const [parameterEditMode, setParameterEditMode] = useState<'simple' | 'html'>('simple')

  // Estados para aba Indicadores
  const [loadingIndicators, setLoadingIndicators] = useState<boolean>(true)
  const [indicatorsFilterScgpId, setIndicatorsFilterScgpId] = useState<string>('')
  const [indicatorsFilterBrokerName, setIndicatorsFilterBrokerName] = useState<string>('')
  const [topProducts, setTopProducts] = useState<Array<{ product_id: string; product_name: string; total_quantity: number; total_points: number }>>([])
  const [topFavoriteProducts, setTopFavoriteProducts] = useState<Array<{ product_id: string; product_name: string; favorite_count: number }>>([])
  const [ordersByStatus, setOrdersByStatus] = useState<Array<{ status: string; count: number; total_points: number }>>([])
  const [ordersByMonth, setOrdersByMonth] = useState<Array<{ month: string; count: number; total_points: number }>>([])
  const [freightByMonth, setFreightByMonth] = useState<Array<{ month: string; total_freight: number }>>([])
  const [deliveryLeadByMonth, setDeliveryLeadByMonth] = useState<
    Array<{ month: string; avg_delivery_days: number }>
  >([])
  const [topBrokers, setTopBrokers] = useState<Array<{ broker_id: string; broker_name?: string; total_orders: number; total_points: number }>>([])
  const [metrics, setMetrics] = useState<{
    total_orders: number
    total_points_disponibilizados: number
    total_points_spent: number
    total_products_sold: number
    average_order_value: number
    total_brokers: number
  }>({
    total_orders: 0,
    total_points_disponibilizados: 0,
    total_points_spent: 0,
    total_products_sold: 0,
    average_order_value: 0,
    total_brokers: 0
  })
  const [indicatorsDateFrom, setIndicatorsDateFrom] = useState<string>('')
  const [indicatorsDateTo, setIndicatorsDateTo] = useState<string>('')
  const [indicatorsRankingTab, setIndicatorsRankingTab] = useState<IndicatorsRankingTabType>('produtos')

  // Buscar título e descrição do módulo
  useEffect(() => {
    const loadModuleInfo = async () => {
      try {
        const result = await modulesService.getByName('portais_shop')
        if (result.success && result.data) {
          if (result.data.display_name) {
            setModuleTitle(result.data.display_name)
          }
          if (result.data.description) {
            setModuleDescription(result.data.description)
          }
        }
      } catch (error) {
        console.error('Erro ao buscar informações do módulo:', error)
      }
    }

    loadModuleInfo()
  }, [])

  // Rota amigável do portal: abre diretamente a relação de corretores do Shop
  useEffect(() => {
    if (location.pathname === '/corretores/relacao') {
      setActiveTab('corretores')
      setCorretoresSubTab('pontuacao')
    }
  }, [location.pathname])

  // Carregar categorias (usado em múltiplas abas)
  const loadCategories = useCallback(async () => {
    setLoadingCategories(true)
    try {
      const data = await shopService.listCategories()
      setCategories(data)
    } catch (error) {
      console.error('Erro ao carregar categorias:', error)
    } finally {
      setLoadingCategories(false)
    }
  }, [])

  useEffect(() => {
    loadCategories()
  }, [loadCategories])

  const handleDeleteCategory = useCallback(
    async (id: string) => {
      if (!window.confirm('Tem certeza que deseja excluir esta categoria?')) return

      setDeletingCategoryId(id)
      try {
        await shopService.deleteCategory(id)
        await loadCategories()
      } catch (error) {
        console.error('Erro ao excluir categoria:', error)
        alert(error instanceof Error ? error.message : 'Erro ao excluir categoria. Tente novamente.')
      } finally {
        setDeletingCategoryId(null)
      }
    },
    [loadCategories],
  )

  // Carregar produtos (aba Produtos e aba Estoque)
  const loadProducts = useCallback(async () => {
    if (activeTab !== 'produtos' && activeTab !== 'estoque') return

    setLoadingProducts(true)
    try {
      const filters: any = {}
      if (productFilterCategory) filters.categoryId = productFilterCategory
      if (productFilterActive !== 'all') filters.isActive = productFilterActive === 'active'
      if (productFilterFeatured !== 'all') filters.isFeatured = productFilterFeatured === 'featured'

      const data = await shopService.listProducts(filters)
      
      // Aplicar filtro de nome no frontend
      let filteredData = data
      if (productFilterName.trim()) {
        const searchTerm = productFilterName.trim().toLowerCase()
        filteredData = data.filter(product => 
          product.name.toLowerCase().includes(searchTerm)
        )
      }
      
      setProducts(filteredData)
    } catch (error) {
      console.error('Erro ao carregar produtos:', error)
    } finally {
      setLoadingProducts(false)
    }
  }, [activeTab, productFilterCategory, productFilterActive, productFilterFeatured, productFilterName])

  useEffect(() => {
    loadProducts()
  }, [loadProducts])

  // Carregar movimentações de estoque
  const loadStockMovements = useCallback(async () => {
    if (activeTab !== 'estoque') return
    setLoadingStockMovements(true)
    try {
      const filters: { productId?: string; movementType?: ShopStockMovementType; dateFrom?: string; dateTo?: string } = {}
      if (stockFilterProductId) filters.productId = stockFilterProductId
      if (stockFilterMovementType !== 'all') filters.movementType = stockFilterMovementType as ShopStockMovementType
      if (stockDateFrom) filters.dateFrom = stockDateFrom
      if (stockDateTo) filters.dateTo = stockDateTo
      const data = await shopService.listStockMovements(filters)
      setStockMovements(data)
    } catch (error) {
      console.error('Erro ao carregar movimentações de estoque:', error)
    } finally {
      setLoadingStockMovements(false)
    }
  }, [activeTab, stockFilterProductId, stockFilterMovementType, stockDateFrom, stockDateTo])

  // Paginação de estoque
  const STOCK_PRODUCTS_PER_PAGE = 20
  const stockProductsTotalPages = Math.max(1, Math.ceil(products.length / STOCK_PRODUCTS_PER_PAGE))
  const stockProductsStartIndex = (stockProductsPage - 1) * STOCK_PRODUCTS_PER_PAGE
  const stockProductsEndIndex = stockProductsStartIndex + STOCK_PRODUCTS_PER_PAGE
  const paginatedStockProducts = products.slice(stockProductsStartIndex, stockProductsEndIndex)

  const STOCK_MOVEMENTS_PER_PAGE = 20
  const stockMovementsTotalPages = Math.max(1, Math.ceil(stockMovements.length / STOCK_MOVEMENTS_PER_PAGE))
  const stockMovementsStartIndex = (stockMovementsPage - 1) * STOCK_MOVEMENTS_PER_PAGE
  const stockMovementsEndIndex = stockMovementsStartIndex + STOCK_MOVEMENTS_PER_PAGE
  const paginatedStockMovements = stockMovements.slice(stockMovementsStartIndex, stockMovementsEndIndex)

  useEffect(() => {
    loadStockMovements()
  }, [loadStockMovements])

  // Histórico de acessos do Shop
  const loadShopHistoricoAcessos = useCallback(async () => {
    if (activeTab !== 'historico') return

    setLoadingShopHistorico(true)
    try {
      const filtrosComuns = {
        corretorQuery: shopCorretorQuery || undefined,
      }

      const [historico, historicoPrimeiroAcesso, total, hoje, brokerStats] = await Promise.all([
        shopService.getHistoricoAcessos({
          ...filtrosComuns,
          dateFrom: shopDateFrom || undefined,
          dateTo: shopDateTo || undefined,
        }),
        shopService.getHistoricoAcessos(filtrosComuns),
        shopService.getTotalAcessos(),
        shopService.getAcessosHoje(),
        shopService.getBrokerAccessStats(3),
      ])

      setShopHistoricoAcessos(historico)
      setShopHistoricoAcessosPrimeiroAcesso(historicoPrimeiroAcesso)
      setShopTotalHistorico(total)
      setShopAcessosHoje(hoje)
      setShopCorretoresComAcesso(brokerStats.distinctCorretores)
      setShopTopCorretoresAcesso(brokerStats.topCorretores)
    } catch (error) {
      console.error('Erro ao carregar histórico de acessos do Shop:', error)
      setShopHistoricoAcessos([])
      setShopHistoricoAcessosPrimeiroAcesso([])
      setShopTotalHistorico(0)
      setShopAcessosHoje(0)
      setShopCorretoresComAcesso(0)
      setShopTopCorretoresAcesso([])
    } finally {
      setLoadingShopHistorico(false)
    }
  }, [activeTab, shopCorretorQuery, shopDateFrom, shopDateTo])

  useEffect(() => {
    loadShopHistoricoAcessos()
  }, [loadShopHistoricoAcessos])

  // Resetar páginas quando filtros mudarem
  useEffect(() => {
    setStockProductsPage(1)
  }, [productFilterCategory, productFilterActive, productFilterFeatured, productFilterName])

  useEffect(() => {
    setStockMovementsPage(1)
  }, [stockFilterProductId, stockFilterMovementType, stockDateFrom, stockDateTo])

  useEffect(() => {
    setOrdersPage(1)
  }, [orderFilterStatus, orderFilterScgpId, orderFilterBrokerName, orderDateFrom, orderDateTo, orderSortBy, orderSortOrder, pedidosViewTab])

  useEffect(() => {
    setBrokersPage(1)
  }, [brokerFilterScgpId, brokerFilterName, brokerSortBy, brokerSortOrder])

  useEffect(() => {
    setHistoryPage(1)
  }, [historyFilterType, historyFilterScgpId, historyFilterBrokerName, historyDateFrom, historyDateTo, historySortBy, historySortOrder])

  useEffect(() => {
    setShopCurrentPage(1)
  }, [shopCorretorQuery, shopDateFrom, shopDateTo])

  // Paginação e ordenação do histórico de acessos do Shop
  const shopFiltrados = useMemo(() => {
    const resultado = [...shopHistoricoAcessos]

    resultado.sort((a, b) => {
      let aValue: any
      let bValue: any

      switch (shopSortBy) {
        case 'corretor':
          aValue = a.corretor || ''
          bValue = b.corretor || ''
          break
        case 'accessed_at':
          aValue = a.accessed_at || ''
          bValue = b.accessed_at || ''
          break
        case 'ip_address':
          aValue = a.ip_address || ''
          bValue = b.ip_address || ''
          break
        case 'user_agent':
          aValue = a.user_agent || ''
          bValue = b.user_agent || ''
          break
        case 'token':
          aValue = a.token || ''
          bValue = b.token || ''
          break
        default:
          aValue = a.accessed_at || ''
          bValue = b.accessed_at || ''
      }

      if (aValue < bValue) return shopSortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return shopSortOrder === 'asc' ? 1 : -1
      return 0
    })

    return resultado
  }, [shopHistoricoAcessos, shopSortBy, shopSortOrder])

  const shopTotalPages = Math.max(1, Math.ceil(shopFiltrados.length / SHOP_ITEMS_PER_PAGE))
  const shopStartIndex = (shopCurrentPage - 1) * SHOP_ITEMS_PER_PAGE
  const shopEndIndex = shopStartIndex + SHOP_ITEMS_PER_PAGE
  const shopFiltradosPaginados = shopFiltrados.slice(shopStartIndex, shopEndIndex)

  const buildShopFilledDailySeries = useCallback(
    (counts: Map<string, number>) => {
      const today = getDateInSPTimezone(new Date())
      const sortedDates = Array.from(counts.keys()).sort()

      if (sortedDates.length === 0 && !shopDateFrom && !shopDateTo) {
        return []
      }

      let startDate = shopDateFrom ? shopDateFrom.split('T')[0] : sortedDates[0] ?? today
      let endDate = shopDateTo ? shopDateTo.split('T')[0] : sortedDates[sortedDates.length - 1] ?? today

      if (!shopDateFrom && !shopDateTo && sortedDates.length > 0) {
        const [y1, m1, d1] = sortedDates[0].split('-').map(Number)
        const [y2, m2, d2] = today.split('-').map(Number)
        const first = new Date(y1, m1 - 1, d1)
        const last = new Date(y2, m2 - 1, d2)
        const spanDays = Math.floor((last.getTime() - first.getTime()) / 86_400_000) + 1
        if (spanDays > HISTORICO_CHART_MAX_DAYS) {
          startDate = getStartDateForLastNDaysInclusive(HISTORICO_CHART_MAX_DAYS)
          endDate = today
        }
      }

      if (startDate > endDate) {
        return []
      }

      return enumerateDateRangeYmd(startDate, endDate).map((date) => ({
        date,
        total: counts.get(date) ?? 0,
      }))
    },
    [shopDateFrom, shopDateTo]
  )

  const shopAcessosPorData = useMemo(() => {
    const counts = new Map<string, number>()
    for (const log of shopHistoricoAcessos) {
      if (!log.accessed_at) continue
      const date = getDateInSPTimezone(new Date(log.accessed_at))
      counts.set(date, (counts.get(date) ?? 0) + 1)
    }
    return buildShopFilledDailySeries(counts)
  }, [shopHistoricoAcessos, buildShopFilledDailySeries])

  const shopCorretoresPrimeiroAcessoPorData = useMemo(() => {
    const primeiroAcessoPorBroker = new Map<string, string>()
    for (const log of shopHistoricoAcessosPrimeiroAcesso) {
      if (!log.broker_id || !log.accessed_at) continue
      const existing = primeiroAcessoPorBroker.get(log.broker_id)
      if (!existing || log.accessed_at < existing) {
        primeiroAcessoPorBroker.set(log.broker_id, log.accessed_at)
      }
    }

    const counts = new Map<string, number>()
    for (const ts of primeiroAcessoPorBroker.values()) {
      const date = getDateInSPTimezone(new Date(ts))
      counts.set(date, (counts.get(date) ?? 0) + 1)
    }
    return buildShopFilledDailySeries(counts)
  }, [shopHistoricoAcessosPrimeiroAcesso, buildShopFilledDailySeries])

  // Carregar pedidos
  const loadOrders = useCallback(async () => {
    setLoadingOrders(true)
    try {
      const filters: any = {}
      if (pedidosViewTab === 'listagem' && orderFilterStatus !== 'all') {
        filters.status = orderFilterStatus
      }
      if (orderFilterScgpId) filters.scgpId = orderFilterScgpId
      if (orderFilterBrokerName) filters.brokerName = orderFilterBrokerName
      if (orderDateFrom) filters.dateFrom = orderDateFrom
      if (orderDateTo) filters.dateTo = orderDateTo

      const data = await shopService.listOrders(filters)
      setOrders(data)
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error)
    } finally {
      setLoadingOrders(false)
    }
  }, [pedidosViewTab, orderFilterStatus, orderFilterScgpId, orderFilterBrokerName, orderDateFrom, orderDateTo])

  useEffect(() => {
    loadOrders()
  }, [loadOrders])

  // Carregar corretores
  const loadBrokers = useCallback(async (filters?: { scgpId: string; brokerName: string }) => {
    const activeFilters = filters ?? appliedBrokerFilters
    setLoadingBrokers(true)
    try {
      const apiFilters: Record<string, string> = {}
      if (activeFilters.scgpId) apiFilters.scgpId = activeFilters.scgpId
      if (activeFilters.brokerName) apiFilters.brokerName = activeFilters.brokerName

      const data = await shopService.listBrokers(apiFilters)
      setBrokers(data)
    } catch (error) {
      console.error('Erro ao carregar corretores:', error)
      setBrokers([])
    } finally {
      setLoadingBrokers(false)
    }
  }, [appliedBrokerFilters])

  const openBrokerModal = useCallback(async (broker: ShopBroker) => {
    setSelectedBrokerForModal(broker)
    setIsBrokerModalOpen(true)
    setBrokerFavoriteProducts([])
    setLoadingBrokerFavorites(true)
    try {
      const favorites = await shopService.getBrokerFavoriteProducts(broker.id)
      setBrokerFavoriteProducts(favorites)
    } catch (error) {
      console.error('Erro ao carregar favoritos do corretor:', error)
      setBrokerFavoriteProducts([])
    } finally {
      setLoadingBrokerFavorites(false)
    }
  }, [])

  const closeBrokerModal = useCallback(() => {
    setIsBrokerModalOpen(false)
    setSelectedBrokerForModal(null)
    setBrokerFavoriteProducts([])
  }, [])

  const fetchShopLink = useCallback(async (broker: ShopBroker): Promise<string> => {
    const codigocorretor = broker.broker?.id_scgp
    if (codigocorretor == null) {
      throw new Error('Este corretor não possui código SCGP (id_scgp).')
    }
    const result = await serverInfoService.getShopLink(codigocorretor)
    if (result.success && result.data) {
      return result.data
    }
    throw new Error(result.error || 'Erro ao gerar link')
  }, [])

  const adaptLinkToHomologacao = (link: string): string => {
    if (!link.startsWith(SHOP_PROD_ORIGIN)) {
      throw new Error('Link do shop em formato inesperado para homologação')
    }
    return `${SHOP_HOMOLOG_ORIGIN}${link.slice(SHOP_PROD_ORIGIN.length)}`
  }

  const handleAcessarShop = useCallback(async (broker: ShopBroker, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!canInsertShop) return
    setAcessandoBrokerId(broker.id)
    try {
      const link = await fetchShopLink(broker)
      window.open(link, '_blank')
      try {
        await userLogsService.logUserAction({
          action: 'view',
          entityType: 'other',
          entityId: broker.id,
          description: `Acessou shop do corretor ${broker.broker?.name || broker.id}`,
          metadata: {
            broker_id: broker.id,
            broker_name: broker.broker?.name,
            id_scgp: broker.broker?.id_scgp,
            link,
            origem: 'portais/shop',
          },
        })
      } catch (logError) {
        console.warn('Erro ao registrar log de acesso ao shop:', logError)
      }
    } catch (error) {
      console.error('Erro ao acessar shop do corretor:', error)
      alert(error instanceof Error ? error.message : 'Erro ao gerar link. Tente novamente.')
    } finally {
      setAcessandoBrokerId(null)
    }
  }, [canInsertShop, fetchShopLink])

  const handleHomologacao = useCallback(async (broker: ShopBroker, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!canInsertShop) return
    setHomologandoBrokerId(broker.id)
    try {
      const link = await fetchShopLink(broker)
      const homologLink = adaptLinkToHomologacao(link)
      window.open(homologLink, '_blank')
      try {
        await userLogsService.logUserAction({
          action: 'view',
          entityType: 'other',
          entityId: broker.id,
          description: `Acessou homologação do shop do corretor ${broker.broker?.name || broker.id}`,
          metadata: {
            broker_id: broker.id,
            broker_name: broker.broker?.name,
            id_scgp: broker.broker?.id_scgp,
            link: homologLink,
            link_original: link,
            ambiente: 'homologacao',
            origem: 'portais/shop',
          },
        })
      } catch (logError) {
        console.warn('Erro ao registrar log de acesso à homologação do shop:', logError)
      }
    } catch (error) {
      console.error('Erro ao acessar homologação do shop do corretor:', error)
      alert(error instanceof Error ? error.message : 'Erro ao gerar link. Tente novamente.')
    } finally {
      setHomologandoBrokerId(null)
    }
  }, [canInsertShop, fetchShopLink])

  const openPointsAdjustModal = useCallback((broker: ShopBroker, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!canUpdateShop) return
    setPointsAdjustBroker(broker)
    setPointsAdjustOperation('add')
    setPointsAdjustQuantity(1)
    setPointsAdjustObservation('')
    setIsPointsAdjustModalOpen(true)
  }, [canUpdateShop])

  const closePointsAdjustModal = useCallback(() => {
    setIsPointsAdjustModalOpen(false)
    setPointsAdjustBroker(null)
    setPointsAdjustOperation('add')
    setPointsAdjustQuantity(1)
    setPointsAdjustObservation('')
    setSavingPointsAdjust(false)
  }, [])

  const pointsAdjustDelta = useMemo(() => {
    const qty = Math.max(0, Math.floor(Number(pointsAdjustQuantity) || 0))
    if (qty <= 0) return 0
    return pointsAdjustOperation === 'add' ? qty : -qty
  }, [pointsAdjustOperation, pointsAdjustQuantity])

  const pointsAdjustProjectedBalance = useMemo(() => {
    if (!pointsAdjustBroker) return 0
    return pointsAdjustBroker.points_balance + pointsAdjustDelta
  }, [pointsAdjustBroker, pointsAdjustDelta])

  // Carregar histórico de PASI Points (aba Corretores, sub-aba Extrato)
  const loadPointsHistory = useCallback(async (filters?: {
    type: string
    scgpId: string
    brokerName: string
    dateFrom: string
    dateTo: string
  }) => {
    const activeFilters = filters ?? appliedHistoryFilters
    setLoadingPointsHistory(true)
    try {
      const apiFilters: Record<string, string> = {}
      if (activeFilters.type !== 'all') apiFilters.transactionType = activeFilters.type
      if (activeFilters.scgpId?.trim()) apiFilters.scgpId = activeFilters.scgpId.trim()
      if (activeFilters.brokerName?.trim()) apiFilters.brokerName = activeFilters.brokerName.trim()
      if (activeFilters.dateFrom) apiFilters.dateFrom = activeFilters.dateFrom
      if (activeFilters.dateTo) apiFilters.dateTo = activeFilters.dateTo

      const data = await shopService.listPointsHistory(apiFilters)
      setPointsHistory(data)
    } catch (error) {
      console.error('Erro ao carregar histórico de PASI Points:', error)
      setPointsHistory([])
    } finally {
      setLoadingPointsHistory(false)
    }
  }, [appliedHistoryFilters])

  // Carregar indicadores
  const loadIndicators = useCallback(async () => {
    if (activeTab !== 'indicadores') return

    setLoadingIndicators(true)
    try {
      const dateFrom = indicatorsDateFrom || undefined
      const dateTo = indicatorsDateTo || undefined
      const scgpId = indicatorsFilterScgpId || undefined
      const brokerName = indicatorsFilterBrokerName || undefined

      const [
        topProductsData,
        topFavoriteProductsData,
        ordersByStatusData,
        ordersByMonthData,
        freightByMonthData,
        deliveryLeadByMonthData,
        topBrokersData,
        metricsData,
      ] = await Promise.all([
        shopService.getTopSellingProducts(10, dateFrom, dateTo),
        shopService.getTopFavoriteProducts(10),
        shopService.getOrdersByStatus(dateFrom, dateTo),
        shopService.getOrdersByMonth(6, dateFrom, dateTo, scgpId, brokerName),
        shopService.getFreightTotalByMonth(6, dateFrom, dateTo, scgpId, brokerName),
        shopService.getAvgDeliveryLeadTimeByMonth(6, dateFrom, dateTo, scgpId, brokerName),
        shopService.getTopBrokers(10, dateFrom, dateTo, scgpId, brokerName),
        shopService.getShopMetrics(dateFrom, dateTo),
      ])

      setTopProducts(topProductsData)
      setTopFavoriteProducts(topFavoriteProductsData)
      setOrdersByStatus(ordersByStatusData)
      setOrdersByMonth(ordersByMonthData)
      setFreightByMonth(freightByMonthData)
      setDeliveryLeadByMonth(deliveryLeadByMonthData)
      setTopBrokers(topBrokersData)
      setMetrics(metricsData)
    } catch (error) {
      console.error('Erro ao carregar indicadores:', error)
    } finally {
      setLoadingIndicators(false)
    }
  }, [activeTab, indicatorsDateFrom, indicatorsDateTo, indicatorsFilterScgpId, indicatorsFilterBrokerName])

  useEffect(() => {
    loadIndicators()
  }, [loadIndicators])

  const handleShopTableWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault()
      if (shopTableScrollRef.current) {
        shopTableScrollRef.current.scrollBy({ left: e.deltaY, behavior: 'auto' })
      }
    }
  }

  const scrollShopTableBy = (dx: number) => {
    if (shopTableScrollRef.current) {
      shopTableScrollRef.current.scrollBy({ left: dx, behavior: 'smooth' })
    }
  }

  // Carregar parâmetros do shop
  const loadParameters = useCallback(async () => {
    if (activeTab !== 'parametros') return

    setLoadingParameters(true)
    try {
      const data = await shopService.listParameters()
      setParameters(data)
    } catch (error) {
      console.error('Erro ao carregar parâmetros:', error)
    } finally {
      setLoadingParameters(false)
    }
  }, [activeTab])

  useEffect(() => {
    loadParameters()
  }, [loadParameters])

  // Funções de ordenação
  const toggleOrderSort = useCallback((key: string) => {
    if (orderSortBy === key) {
      setOrderSortOrder(orderSortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setOrderSortBy(key)
      setOrderSortOrder('asc')
    }
  }, [orderSortBy, orderSortOrder])

  const toggleBrokerSort = useCallback((key: string) => {
    if (brokerSortBy === key) {
      setBrokerSortOrder(brokerSortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setBrokerSortBy(key)
      setBrokerSortOrder('asc')
    }
  }, [brokerSortBy, brokerSortOrder])

  const toggleHistorySort = useCallback((key: string) => {
    if (historySortBy === key) {
      setHistorySortOrder(historySortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setHistorySortBy(key)
      setHistorySortOrder('asc')
    }
  }, [historySortBy, historySortOrder])

  // Funções para limpar filtros
  const clearProductFilters = useCallback(() => {
    setProductFilterCategory('')
    setProductFilterActive('all')
    setProductFilterFeatured('all')
    setProductFilterName('')
  }, [])

  const clearOrderFilters = useCallback(() => {
    setOrderFilterStatus('all')
    setOrderFilterScgpId('')
    setOrderFilterBrokerName('')
    setOrderDateFrom('')
    setOrderDateTo('')
  }, [])

  const clearBrokerFilters = useCallback(() => {
    setBrokerFilterScgpId('')
    setBrokerFilterName('')
    setAppliedBrokerFilters({ scgpId: '', brokerName: '' })
    setHasSearchedBrokers(false)
    setBrokers([])
    setBrokersPage(1)
  }, [])

  const clearHistoryFilters = useCallback(() => {
    setHistoryFilterType('all')
    setHistoryFilterScgpId('')
    setHistoryFilterBrokerName('')
    setHistoryDateFrom('')
    setHistoryDateTo('')
    setAppliedHistoryFilters({
      type: 'all',
      scgpId: '',
      brokerName: '',
      dateFrom: '',
      dateTo: '',
    })
    setHasSearchedHistory(false)
    setPointsHistory([])
    setHistoryPage(1)
  }, [])

  const hasBrokerFiltersActive = useMemo(
    () => !!(brokerFilterScgpId.trim() || brokerFilterName.trim()),
    [brokerFilterScgpId, brokerFilterName]
  )

  const hasHistoryFiltersActive = useMemo(
    () =>
      !!(
        historyFilterType !== 'all' ||
        historyFilterScgpId.trim() ||
        historyFilterBrokerName.trim() ||
        historyDateFrom ||
        historyDateTo
      ),
    [historyFilterType, historyFilterScgpId, historyFilterBrokerName, historyDateFrom, historyDateTo]
  )

  const applyBrokerFilters = useCallback(() => {
    if (!hasBrokerFiltersActive) return

    const filters = {
      scgpId: brokerFilterScgpId,
      brokerName: brokerFilterName,
    }
    setAppliedBrokerFilters(filters)
    setBrokersPage(1)
    setHasSearchedBrokers(true)
    void loadBrokers(filters)
  }, [hasBrokerFiltersActive, brokerFilterScgpId, brokerFilterName, loadBrokers])

  const applyHistoryFilters = useCallback(() => {
    if (!hasHistoryFiltersActive) return

    const filters = {
      type: historyFilterType,
      scgpId: historyFilterScgpId,
      brokerName: historyFilterBrokerName,
      dateFrom: historyDateFrom,
      dateTo: historyDateTo,
    }
    setAppliedHistoryFilters(filters)
    setHistoryPage(1)
    setHasSearchedHistory(true)
    void loadPointsHistory(filters)
  }, [
    hasHistoryFiltersActive,
    historyFilterType,
    historyFilterScgpId,
    historyFilterBrokerName,
    historyDateFrom,
    historyDateTo,
    loadPointsHistory,
  ])

  const clearIndicatorsFilters = useCallback(() => {
    setIndicatorsDateFrom('')
    setIndicatorsDateTo('')
    setIndicatorsFilterScgpId('')
    setIndicatorsFilterBrokerName('')
  }, [])

  // Aplicar ordenação
  const sortedOrders = useMemo(() => {
    const sorted = [...orders]
    sorted.sort((a, b) => {
      let aValue: any
      let bValue: any

      switch (orderSortBy) {
        case 'order_number':
          aValue = a.order_number || ''
          bValue = b.order_number || ''
          break
        case 'broker_id':
          aValue = a.broker?.name || ''
          bValue = b.broker?.name || ''
          break
        case 'total_points':
          aValue = a.total_points || 0
          bValue = b.total_points || 0
          break
        case 'status':
          aValue = a.status || ''
          bValue = b.status || ''
          break
        case 'created_at':
        default:
          aValue = a.created_at || ''
          bValue = b.created_at || ''
      }

      if (aValue < bValue) return orderSortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return orderSortOrder === 'asc' ? 1 : -1
      return 0
    })

    return sorted
  }, [orders, orderSortBy, orderSortOrder])

  const ordersGroupedForKanban = useMemo(() => {
    const groups: Record<ShopOrderStatus, ShopOrder[]> = {
      pending: [],
      confirmed: [],
      shipped: [],
      delivered: [],
      cancelled: [],
    }
    for (const order of orders) {
      if (groups[order.status]) {
        groups[order.status].push(order)
      }
    }
    const byCreatedDesc = (a: ShopOrder, b: ShopOrder) => {
      const ta = a.created_at ?? ''
      const tb = b.created_at ?? ''
      if (ta < tb) return 1
      if (ta > tb) return -1
      return 0
    }
    for (const k of Object.keys(groups) as ShopOrderStatus[]) {
      groups[k].sort(byCreatedDesc)
    }
    return groups
  }, [orders])

  // Paginação de pedidos
  const ORDERS_PER_PAGE = 20
  const ordersTotalPages = Math.max(1, Math.ceil(sortedOrders.length / ORDERS_PER_PAGE))
  const ordersStartIndex = (ordersPage - 1) * ORDERS_PER_PAGE
  const ordersEndIndex = ordersStartIndex + ORDERS_PER_PAGE
  const paginatedOrders = sortedOrders.slice(ordersStartIndex, ordersEndIndex)

  const sortedBrokers = useMemo(() => {
    const sorted = [...brokers]
    sorted.sort((a, b) => {
      let aValue: any
      let bValue: any

      switch (brokerSortBy) {
        case 'broker_id':
          aValue = a.broker?.name || ''
          bValue = b.broker?.name || ''
          break
        case 'points_balance':
          aValue = a.points_balance || 0
          bValue = b.points_balance || 0
          break
        case 'total_points_earned':
          aValue = a.total_points_earned || 0
          bValue = b.total_points_earned || 0
          break
        case 'total_points_removed':
          aValue = a.total_points_removed || 0
          bValue = b.total_points_removed || 0
          break
        case 'total_points_spent':
          aValue = a.total_points_spent || 0
          bValue = b.total_points_spent || 0
          break
        case 'created_at':
        default:
          aValue = a.created_at || ''
          bValue = b.created_at || ''
      }

      if (aValue < bValue) return brokerSortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return brokerSortOrder === 'asc' ? 1 : -1
      return 0
    })

    return sorted
  }, [brokers, brokerSortBy, brokerSortOrder])

  // Paginação de corretores
  const BROKERS_PER_PAGE = 20
  const brokersTotalPages = Math.max(1, Math.ceil(sortedBrokers.length / BROKERS_PER_PAGE))
  const brokersStartIndex = (brokersPage - 1) * BROKERS_PER_PAGE
  const brokersEndIndex = brokersStartIndex + BROKERS_PER_PAGE
  const paginatedBrokers = sortedBrokers.slice(brokersStartIndex, brokersEndIndex)

  const sortedPointsHistory = useMemo(() => {
    const sorted = [...pointsHistory]
    sorted.sort((a, b) => {
      let aValue: any
      let bValue: any

      switch (historySortBy) {
        case 'broker_id':
          aValue = a.broker?.name || ''
          bValue = b.broker?.name || ''
          break
        case 'transaction_type':
          aValue = a.transaction_type || ''
          bValue = b.transaction_type || ''
          break
        case 'points_amount':
          aValue = a.points_amount || 0
          bValue = b.points_amount || 0
          break
        case 'created_at':
        default:
          aValue = a.created_at || ''
          bValue = b.created_at || ''
      }

      if (aValue < bValue) return historySortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return historySortOrder === 'asc' ? 1 : -1
      return 0
    })

    return sorted
  }, [pointsHistory, historySortBy, historySortOrder])

  // Paginação do histórico de pontos
  const HISTORY_PER_PAGE = 25
  const historyTotalPages = Math.max(1, Math.ceil(sortedPointsHistory.length / HISTORY_PER_PAGE))
  const historyStartIndex = (historyPage - 1) * HISTORY_PER_PAGE
  const historyEndIndex = historyStartIndex + HISTORY_PER_PAGE
  const paginatedHistory = sortedPointsHistory.slice(historyStartIndex, historyEndIndex)

  // Função auxiliar para formatar data
  const formatDateTime = (iso: string | null) => {
    if (!iso) return '-'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '-'
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  }

  const formatDate = (iso: string | null) => {
    if (!iso) return '-'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '-'
    return d.toLocaleDateString('pt-BR')
  }

  const formatDateShort = (dateString: string) => {
    const [y, m, d] = dateString.split('-').map(Number)
    if (!y || !m || !d) return dateString
    return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`
  }

  const formatShopChartDateTick = (dateString: string) => {
    const [, m, d] = dateString.split('-')
    return `${d}/${m}`
  }

  const truncateShopCorretorName = (name: string, maxChars = 48) => {
    if (!name) return ''
    return name.length > maxChars ? `${name.slice(0, maxChars)}...` : name
  }

  return (
    <div className="h-full flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard
          moduleName="portais_shop"
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">Acesso Negado</Text>
                <Text size="sm" color="muted">Você não tem permissão para acessar esta página.</Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-4 min-w-0">
                <img
                  src="/Logo_PASI_SHOP-03.png"
                  alt="PASI Shop"
                  className="h-14 w-auto flex-shrink-0 object-contain select-none"
                />
                <div className="min-w-0">
                  <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">{moduleTitle}</Heading>
                  <Text size="sm" color="muted" className="mt-2">{moduleDescription}</Text>
                </div>
              </div>
            </div>

            {/* Navegação das Abas */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab('pedidos')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'pedidos'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Pedidos
                  {!loadingOrders ? (
                    <span className="ml-1.5 tabular-nums font-semibold">({orders.length})</span>
                  ) : null}
                </button>
                <button
                  onClick={() => setActiveTab('indicadores')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'indicadores'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Indicadores
                </button>
                <button
                  onClick={() => setActiveTab('historico')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'historico'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Histórico de Acessos
                </button>
                <button
                  onClick={() => setActiveTab('produtos')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'produtos'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Produtos
                </button>
                <button
                  onClick={() => setActiveTab('estoque')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'estoque'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Estoque
                </button>
                <button
                  onClick={() => setActiveTab('corretores')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'corretores'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Corretores
                </button>
                <button
                  onClick={() => setActiveTab('parametros')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'parametros'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Parâmetros
                </button>
              </nav>
            </div>

            {/* Conteúdo das Abas */}
            {activeTab === 'produtos' && (
              <div className="flex-1 flex flex-col overflow-y-auto">
                <div className="py-6 px-4 sm:px-6 lg:px-8">
                  {/* Sub-abas da aba Produtos */}
                  <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                    <nav className="flex space-x-6">
                      <button
                        onClick={() => setProductSubTab('cadastro')}
                        className={`py-3 px-1 border-b-2 font-medium text-sm ${
                          productSubTab === 'cadastro'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                      >
                        Cadastro
                      </button>
                      <button
                        onClick={() => setProductSubTab('categorias')}
                        className={`py-3 px-1 border-b-2 font-medium text-sm ${
                          productSubTab === 'categorias'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                      >
                        Categorias
                      </button>
                    </nav>
                  </div>

                  {/* Sub-aba Cadastro (produtos) */}
                  {productSubTab === 'cadastro' && (
                    <>
                  <div className="mb-6 flex items-start justify-between gap-4">
                    <div>
                      <Text size="sm" color="muted">
                        Gerencie os produtos disponíveis para troca por PASI Points
                      </Text>
                    </div>
                    {canInsertShop && (
                      <Button variant="solid" onClick={() => {
                        setEditingProduct({
                          name: '',
                          description: '',
                          category_id: '',
                          points_cost: 0,
                          is_active: true,
                          is_featured: false,
                          is_new: false,
                          requires_size: false,
                          available_sizes: [],
                        })
                        setIsProductModalOpen(true)
                      }}>
                        Novo Produto
                      </Button>
                    )}
                  </div>

                  {/* Filtros */}
                  <div className="mb-4 bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Nome do Produto</Text>
                        <Input
                          type="text"
                          placeholder="Pesquisar por nome..."
                          value={productFilterName}
                          onChange={(e) => setProductFilterName(e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Categoria</Text>
                        <select
                          className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                          value={productFilterCategory}
                          onChange={(e) => setProductFilterCategory(e.target.value)}
                        >
                          <option value="">Todas</option>
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Status</Text>
                        <select
                          className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                          value={productFilterActive}
                          onChange={(e) => setProductFilterActive(e.target.value)}
                        >
                          <option value="all">Todos</option>
                          <option value="active">Ativos</option>
                          <option value="inactive">Inativos</option>
                        </select>
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Destaque</Text>
                        <select
                          className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                          value={productFilterFeatured}
                          onChange={(e) => setProductFilterFeatured(e.target.value)}
                        >
                          <option value="all">Todos</option>
                          <option value="featured">Em Destaque</option>
                          <option value="not-featured">Não Destacados</option>
                        </select>
                      </div>
                      <div className="flex items-end justify-end">
                        <Button variant="outline" onClick={clearProductFilters}>
                          Limpar Filtros
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Listagem de Produtos */}
                  {loadingProducts ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : products.length === 0 ? (
                    <div className="text-center py-12">
                      <Text size="sm" color="muted">Nenhum produto encontrado.</Text>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {products.map((product) => {
                        const primaryImage = product.images?.find(img => img.is_primary) || product.images?.[0]
                        return (
                          <div
                            key={product.id}
                            className={`bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden hover:shadow-lg transition-shadow ${canUpdateShop ? 'cursor-pointer' : 'cursor-default'}`}
                            onClick={() => {
                              if (!canUpdateShop) return
                              setEditingProduct(product)
                              setIsProductModalOpen(true)
                            }}
                          >
                            {primaryImage && (
                              <div className="h-48 bg-gray-200 dark:bg-gray-700 overflow-hidden relative">
                                <ProductImageDisplay image={primaryImage} />
                                <div className="absolute top-2 left-2 flex flex-col gap-2">
                                  {product.is_featured && (
                                    <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded shadow-md">
                                      Destaque
                                    </span>
                                  )}
                                  {product.is_new && (
                                    <span className="px-2 py-1 text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200 rounded shadow-md">
                                      Novo
                                    </span>
                                  )}
                                  <span className={`px-2 py-1 text-xs font-medium rounded shadow-md ${
                                    product.is_active
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  }`}>
                                    {product.is_active ? 'Ativo' : 'Inativo'}
                                  </span>
                                  {(product.stock_quantity ?? 0) <= 0 && (
                                    <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 rounded shadow-md">
                                      Indisponível
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                            {!primaryImage && (
                              <div className="h-48 bg-gray-200 dark:bg-gray-700 relative">
                                <div className="absolute top-2 left-2 flex flex-col gap-2">
                                  {product.is_featured && (
                                    <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded shadow-md">
                                      Destaque
                                    </span>
                                  )}
                                  {product.is_new && (
                                    <span className="px-2 py-1 text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200 rounded shadow-md">
                                      Novo
                                    </span>
                                  )}
                                  <span className={`px-2 py-1 text-xs font-medium rounded shadow-md ${
                                    product.is_active
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  }`}>
                                    {product.is_active ? 'Ativo' : 'Inativo'}
                                  </span>
                                  {(product.stock_quantity ?? 0) <= 0 && (
                                    <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 rounded shadow-md">
                                      Indisponível
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                            <div className="p-4">
                              <div className="mb-2">
                                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                                  {product.name}
                                </Heading>
                              </div>
                              {product.category && (
                                <Text size="xs" color="muted" className="mb-2">
                                  {product.category.name}
                                </Text>
                              )}
                              <div className="flex items-center justify-between mt-4">
                                <div>
                                  <Text size="lg" className="text-primary-600 dark:text-primary-400 font-bold">
                                    {product.points_cost.toLocaleString('pt-BR')} PASI Points
                                  </Text>
                                </div>
                              </div>
                              <Text size="sm" color="muted" className="mt-2">
                                Estoque: {product.stock_quantity ?? 0}
                              </Text>
                              {product.requires_size && (product.available_sizes?.length ?? 0) > 0 && (
                                <Text size="xs" color="muted" className="mt-1">
                                  Tamanhos: {product.available_sizes.join(', ')}
                                </Text>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                    </>
                  )}

                  {/* Sub-aba Categorias */}
                  {productSubTab === 'categorias' && (
                    <>
                  <div className="mb-6 flex items-start justify-between gap-4">
                    <div>
                      <Text size="sm" color="muted">
                        Gerencie as categorias de produtos
                      </Text>
                    </div>
                    <PermissionGuard moduleName="portais_shop" permissionType="insert" fallback={null}>
                      <Button variant="solid" onClick={() => {
                        setEditingCategory({
                          name: '',
                          description: '',
                          slug: '',
                          is_active: true,
                          display_order: categories.length
                        })
                        setIsCategoryModalOpen(true)
                      }}>
                        Nova Categoria
                      </Button>
                    </PermissionGuard>
                  </div>

                  {/* Tabela de Categorias */}
                  {loadingCategories ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900/30">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ordem</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-4 py-2"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {categories.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                Nenhuma categoria encontrada.
                              </td>
                            </tr>
                          ) : (
                            categories.map((cat) => (
                              <tr key={cat.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{cat.name}</td>
                                <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 font-mono text-xs">{cat.slug}</td>
                                <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{cat.display_order}</td>
                                <td className="px-4 py-2 text-sm">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    cat.is_active
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  }`}>
                                    {cat.is_active ? 'Ativa' : 'Inativa'}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-right space-x-2">
                                  <PermissionGuard moduleName="portais_shop" permissionType="update" fallback={null}>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setEditingCategory(cat)
                                        setIsCategoryModalOpen(true)
                                      }}
                                    >
                                      Editar
                                    </Button>
                                  </PermissionGuard>

                                  <PermissionGuard moduleName="portais_shop" permissionType="delete" fallback={null}>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      disabled={deletingCategoryId === cat.id}
                                      onClick={() => handleDeleteCategory(cat.id)}
                                    >
                                      Excluir
                                    </Button>
                                  </PermissionGuard>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                    </>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'estoque' && (
              <div className="flex-1 flex flex-col overflow-y-auto">
                <div className="py-6 px-4 sm:px-6 lg:px-8">
                  <div className="mb-6">
                    <Text size="sm" color="muted">
                      Gestão de estoque dos produtos. Registre entradas, saídas ou ajustes e consulte o histórico.
                    </Text>
                  </div>

                  {/* Sub-abas da aba Estoque */}
                  <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                    <nav className="flex space-x-6">
                      <button
                        onClick={() => setStockSubTab('produtos')}
                        className={`py-3 px-1 border-b-2 font-medium text-sm ${
                          stockSubTab === 'produtos'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                      >
                        Produtos e estoque atual
                      </button>
                      <button
                        onClick={() => setStockSubTab('historico')}
                        className={`py-3 px-1 border-b-2 font-medium text-sm ${
                          stockSubTab === 'historico'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                      >
                        Histórico de movimentações
                      </button>
                    </nav>
                  </div>

                  {/* Conteúdo: Produtos e estoque atual */}
                  {stockSubTab === 'produtos' && (
                    <>
                      {loadingProducts ? (
                        <div className="flex items-center justify-center py-12">
                          <LoadingSpinner />
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-900/30">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Produto</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Estoque</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {products.length === 0 ? (
                                <tr>
                                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                    Nenhum produto encontrado.
                                  </td>
                                </tr>
                              ) : (
                              paginatedStockProducts.map((p) => (
                                  <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{p.name}</td>
                                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{p.category?.name ?? '—'}</td>
                                    <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white">{p.stock_quantity ?? 0}</td>
                                    <td className="px-4 py-2 text-right">
                                      {canUpdateShop && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            setStockModalProduct(p)
                                            setStockMovementType('in')
                                            setStockMovementQuantity(1)
                                            setStockMovementDescription('')
                                            setIsStockModalOpen(true)
                                          }}
                                        >
                                          Movimentar
                                        </Button>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {!loadingProducts && products.length > 0 && stockProductsTotalPages > 1 && (
                        <div className="mt-4 flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
                          <Text size="sm">
                            Mostrando {stockProductsStartIndex + 1} a {Math.min(stockProductsEndIndex, products.length)} de {products.length} produtos
                          </Text>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setStockProductsPage((p) => Math.max(1, p - 1))}
                              disabled={stockProductsPage === 1}
                              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                            >
                              Anterior
                            </button>
                            <div className="flex space-x-1">
                              {Array.from({ length: Math.min(5, stockProductsTotalPages) }, (_, i) => {
                                let pageNumber: number
                                if (stockProductsTotalPages <= 5) {
                                  pageNumber = i + 1
                                } else if (stockProductsPage <= 3) {
                                  pageNumber = i + 1
                                } else if (stockProductsPage >= stockProductsTotalPages - 2) {
                                  pageNumber = stockProductsTotalPages - 4 + i
                                } else {
                                  pageNumber = stockProductsPage - 2 + i
                                }
                                return (
                                  <button
                                    key={pageNumber}
                                    onClick={() => setStockProductsPage(pageNumber)}
                                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                                      stockProductsPage === pageNumber
                                        ? 'bg-primary-600 text-white'
                                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                    }`}
                                  >
                                    {pageNumber}
                                  </button>
                                )
                              })}
                            </div>
                            <button
                              onClick={() => setStockProductsPage((p) => Math.min(stockProductsTotalPages, p + 1))}
                              disabled={stockProductsPage === stockProductsTotalPages}
                              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                            >
                              Próximo
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Conteúdo: Histórico de movimentações */}
                  {stockSubTab === 'historico' && (
                    <>
                      <div className="mb-4 flex flex-wrap items-end gap-4">
                        <div>
                          <Text size="xs" className="text-gray-600 dark:text-gray-400 mb-1 block">Produto</Text>
                          <select
                            value={stockFilterProductId}
                            onChange={(e) => setStockFilterProductId(e.target.value)}
                            className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 min-w-[180px]"
                          >
                            <option value="">Todos</option>
                            {products.map((p) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Text size="xs" className="text-gray-600 dark:text-gray-400 mb-1 block">Tipo</Text>
                          <select
                            value={stockFilterMovementType}
                            onChange={(e) => setStockFilterMovementType(e.target.value)}
                            className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                          >
                            <option value="all">Todos</option>
                            <option value="in">Entrada</option>
                            <option value="out">Saída</option>
                            <option value="adjustment">Ajuste</option>
                          </select>
                        </div>
                        <div>
                          <Text size="xs" className="text-gray-600 dark:text-gray-400 mb-1 block">De</Text>
                          <Input
                            type="date"
                            value={stockDateFrom}
                            onChange={(e) => setStockDateFrom(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="xs" className="text-gray-600 dark:text-gray-400 mb-1 block">Até</Text>
                          <Input
                            type="date"
                            value={stockDateTo}
                            onChange={(e) => setStockDateTo(e.target.value)}
                            className="w-full"
                          />
                        </div>
                      </div>
                      {loadingStockMovements ? (
                        <div className="flex items-center justify-center py-8">
                          <LoadingSpinner />
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-900/30">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Produto</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qtd</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Antes</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Depois</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Referência</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Usuário</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {stockMovements.length === 0 ? (
                                <tr>
                                  <td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                    Nenhuma movimentação encontrada.
                                  </td>
                                </tr>
                              ) : (
                                paginatedStockMovements.map((m) => (
                                  <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                                      {new Date(m.created_at).toLocaleString('pt-BR')}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                                      {(m.product as { name?: string })?.name ?? m.product_id}
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                        m.movement_type === 'in'
                                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                          : m.movement_type === 'out'
                                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                      }`}>
                                        {m.movement_type === 'in' ? 'Entrada' : m.movement_type === 'out' ? 'Saída' : 'Ajuste'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-900 dark:text-white">{m.quantity}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-600 dark:text-gray-300">{m.quantity_before ?? '—'}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-600 dark:text-gray-300">{m.quantity_after ?? '—'}</td>
                                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                                      {m.reference_type === 'order' ? 'Pedido' : m.reference_type === 'manual' ? 'Manual' : m.reference_type === 'adjustment' ? 'Ajuste' : m.reference_type ?? '—'}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                                      {(m as ShopStockMovement).created_by_user?.name ?? (m as ShopStockMovement).created_by ?? '—'}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">{m.description ?? '—'}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {!loadingStockMovements && stockMovements.length > 0 && stockMovementsTotalPages > 1 && (
                        <div className="mt-4 flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
                          <Text size="sm">
                            Mostrando {stockMovementsStartIndex + 1} a {Math.min(stockMovementsEndIndex, stockMovements.length)} de {stockMovements.length} movimentações
                          </Text>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setStockMovementsPage((p) => Math.max(1, p - 1))}
                              disabled={stockMovementsPage === 1}
                              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                            >
                              Anterior
                            </button>
                            <div className="flex space-x-1">
                              {Array.from({ length: Math.min(5, stockMovementsTotalPages) }, (_, i) => {
                                let pageNumber: number
                                if (stockMovementsTotalPages <= 5) {
                                  pageNumber = i + 1
                                } else if (stockMovementsPage <= 3) {
                                  pageNumber = i + 1
                                } else if (stockMovementsPage >= stockMovementsTotalPages - 2) {
                                  pageNumber = stockMovementsTotalPages - 4 + i
                                } else {
                                  pageNumber = stockMovementsPage - 2 + i
                                }
                                return (
                                  <button
                                    key={pageNumber}
                                    onClick={() => setStockMovementsPage(pageNumber)}
                                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                                      stockMovementsPage === pageNumber
                                        ? 'bg-primary-600 text-white'
                                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                    }`}
                                  >
                                    {pageNumber}
                                  </button>
                                )
                              })}
                            </div>
                            <button
                              onClick={() => setStockMovementsPage((p) => Math.min(stockMovementsTotalPages, p + 1))}
                              disabled={stockMovementsPage === stockMovementsTotalPages}
                              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                            >
                              Próximo
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'pedidos' && (
              <div className="flex-1 flex flex-col overflow-y-auto">
                <div className="py-6 px-4 sm:px-6 lg:px-8">
                  <div className="mb-6">
                    <Text size="sm" color="muted">
                      Visualize e gerencie os pedidos realizados pelos corretores
                    </Text>
                  </div>

                  {/* Filtros */}
                  <div className="mb-4 bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Status</Text>
                        <select
                          disabled={pedidosViewTab === 'kanban'}
                          title={pedidosViewTab === 'kanban' ? 'No modo Kanban, os pedidos são agrupados por colunas de status' : undefined}
                          className={`w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 ${
                            pedidosViewTab === 'kanban' ? 'opacity-60 cursor-not-allowed' : ''
                          }`}
                          value={orderFilterStatus}
                          onChange={(e) => setOrderFilterStatus(e.target.value)}
                        >
                          <option value="all">Todos</option>
                          <option value="pending">Pendente</option>
                          <option value="confirmed">Confirmado</option>
                          <option value="shipped">Enviado</option>
                          <option value="delivered">Entregue</option>
                          <option value="cancelled">Cancelado</option>
                        </select>
                        {pedidosViewTab === 'kanban' && (
                          <Text size="xs" color="muted" className="mt-1">
                            Kanban usa todas as situações; use a aba Listagem para filtrar por um status.
                          </Text>
                        )}
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">ID SCGP</Text>
                        <Input
                          type="text"
                          placeholder="ID SCGP do corretor..."
                          value={orderFilterScgpId}
                          onChange={(e) => setOrderFilterScgpId(e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Nome do Corretor</Text>
                        <Input
                          type="text"
                          placeholder="Nome do corretor..."
                          value={orderFilterBrokerName}
                          onChange={(e) => setOrderFilterBrokerName(e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Inicial</Text>
                        <Input
                          type="date"
                          value={orderDateFrom ? orderDateFrom.split('T')[0] : ''}
                          onChange={(e) => {
                            const dateValue = e.target.value
                            setOrderDateFrom(dateValue ? `${dateValue}T00:00:00` : '')
                          }}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Final</Text>
                        <Input
                          type="date"
                          value={orderDateTo ? orderDateTo.split('T')[0] : ''}
                          onChange={(e) => {
                            const dateValue = e.target.value
                            setOrderDateTo(dateValue ? `${dateValue}T23:59:59` : '')
                          }}
                          className="w-full"
                        />
                      </div>
                      <div className="flex items-end justify-end">
                        <Button variant="outline" onClick={clearOrderFilters}>
                          Limpar Filtros
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
                    <nav className="flex space-x-6">
                      <button
                        type="button"
                        onClick={() => setPedidosViewTab('listagem')}
                        className={`py-3 px-1 border-b-2 font-medium text-sm ${
                          pedidosViewTab === 'listagem'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                      >
                        Listagem
                      </button>
                      <button
                        type="button"
                        onClick={() => setPedidosViewTab('kanban')}
                        className={`py-3 px-1 border-b-2 font-medium text-sm ${
                          pedidosViewTab === 'kanban'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                      >
                        Kanban
                      </button>
                    </nav>
                  </div>

                  {/* Tabela de Pedidos */}
                  {loadingOrders ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : pedidosViewTab === 'listagem' ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                          <tr>
                            <SortableTableHeader
                              sortKey="order_number"
                              currentSort={orderSortBy}
                              sortOrder={orderSortOrder}
                              onSort={toggleOrderSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Número do Pedido
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="broker_id"
                              currentSort={orderSortBy}
                              sortOrder={orderSortOrder}
                              onSort={toggleOrderSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Corretor
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="total_points"
                              currentSort={orderSortBy}
                              sortOrder={orderSortOrder}
                              onSort={toggleOrderSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Total de PASI Points
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="status"
                              currentSort={orderSortBy}
                              sortOrder={orderSortOrder}
                              onSort={toggleOrderSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Status
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="created_at"
                              currentSort={orderSortBy}
                              sortOrder={orderSortOrder}
                              onSort={toggleOrderSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Data
                            </SortableTableHeader>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                              Ações
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {sortedOrders.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                Nenhum pedido encontrado.
                              </td>
                            </tr>
                          ) : (
                            paginatedOrders.map((order) => (
                              <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-4 py-4 text-sm font-mono text-gray-900 dark:text-white">
                                  {order.order_number}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                                  {order.broker?.name}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                                  {order.total_points.toLocaleString('pt-BR')}
                                </td>
                                <td className="px-4 py-4 text-sm">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    order.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                    order.status === 'confirmed' ? 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200' :
                                    order.status === 'shipped' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                                    order.status === 'delivered' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  }`}>
                                    {order.status === 'pending' ? 'Pendente' :
                                     order.status === 'confirmed' ? 'Confirmado' :
                                     order.status === 'shipped' ? 'Enviado' :
                                     order.status === 'delivered' ? 'Entregue' :
                                     'Cancelado'}
                                  </span>
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                                  {formatDateTime(order.created_at)}
                                </td>
                                <td className="px-4 py-4 text-sm">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedOrder(order)
                                      setIsOrderDetailModalOpen(true)
                                    }}
                                  >
                                    Ver Detalhes
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="flex gap-4 overflow-x-auto pb-2">
                      {PEDIDOS_KANBAN_COLUMNS.map(
                        ({ status, label, columnClass, headerClass, headerTitleClass, bodyTintClass }) => {
                        const columnOrders = ordersGroupedForKanban[status]
                        return (
                          <div
                            key={status}
                            className={`min-w-[180px] flex-shrink-0 flex flex-col rounded-lg overflow-hidden ${columnClass}`}
                          >
                            <div className={`px-3 py-2 rounded-t-lg ${headerClass}`}>
                              <Text size="sm" className={`font-semibold ${headerTitleClass}`}>
                                {label}
                              </Text>
                              <Text size="xs" className={`opacity-80 ${headerTitleClass}`}>
                                {columnOrders.length} pedido{columnOrders.length === 1 ? '' : 's'}
                              </Text>
                            </div>
                            <div
                              className={`p-2 space-y-2 flex-1 max-h-[min(70vh,720px)] overflow-y-auto ${bodyTintClass}`}
                            >
                              {columnOrders.length === 0 ? (
                                <Text size="sm" color="muted" className="px-2 py-4 text-center block">
                                  Nenhum pedido neste status.
                                </Text>
                              ) : (
                                columnOrders.map((order) => (
                                  <div
                                    key={order.id}
                                    className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 shadow-sm"
                                  >
                                    <Text
                                      size="sm"
                                      className="text-gray-800 dark:text-gray-200 mb-1"
                                      title={order.broker?.name || undefined}
                                    >
                                      {truncateKanbanBrokerLabel(order.broker?.name ?? '—', 20)}
                                    </Text>
                                    <Text size="sm" color="muted" className="mb-2">
                                      {order.total_points.toLocaleString('pt-BR')} PASI Points
                                    </Text>
                                    <Text size="xs" color="muted" className="mb-3">
                                      {formatDateTime(order.created_at)}
                                    </Text>
                                    <a
                                      href="#"
                                      className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline dark:text-primary-400 dark:hover:text-primary-300"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        setSelectedOrder(order)
                                        setIsOrderDetailModalOpen(true)
                                      }}
                                    >
                                      Ver detalhes
                                    </a>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                  {!loadingOrders && pedidosViewTab === 'listagem' && sortedOrders.length > 0 && ordersTotalPages > 1 && (
                    <div className="mt-4 flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
                      <Text size="sm">
                        Mostrando {ordersStartIndex + 1} a {Math.min(ordersEndIndex, sortedOrders.length)} de {sortedOrders.length} pedidos
                      </Text>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setOrdersPage((p) => Math.max(1, p - 1))}
                          disabled={ordersPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Anterior
                        </button>
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, ordersTotalPages) }, (_, i) => {
                            let pageNumber: number
                            if (ordersTotalPages <= 5) {
                              pageNumber = i + 1
                            } else if (ordersPage <= 3) {
                              pageNumber = i + 1
                            } else if (ordersPage >= ordersTotalPages - 2) {
                              pageNumber = ordersTotalPages - 4 + i
                            } else {
                              pageNumber = ordersPage - 2 + i
                            }
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => setOrdersPage(pageNumber)}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                  ordersPage === pageNumber
                                    ? 'bg-primary-600 text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            )
                          })}
                        </div>
                        <button
                          onClick={() => setOrdersPage((p) => Math.min(ordersTotalPages, p + 1))}
                          disabled={ordersPage === ordersTotalPages}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Próximo
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'corretores' && (
              <div className="flex-1 flex flex-col overflow-y-auto">
                <div className="py-6 px-4 sm:px-6 lg:px-8">
                  {/* Sub-abas da aba Corretores */}
                  <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                    <nav className="flex space-x-6">
                      <button
                        onClick={() => setCorretoresSubTab('pontuacao')}
                        className={`py-3 px-1 border-b-2 font-medium text-sm ${
                          corretoresSubTab === 'pontuacao'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                      >
                        Pontuação
                      </button>
                      <button
                        onClick={() => setCorretoresSubTab('historico')}
                        className={`py-3 px-1 border-b-2 font-medium text-sm ${
                          corretoresSubTab === 'historico'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                      >
                        Extrato
                      </button>
                    </nav>
                  </div>

                  {/* Sub-aba Pontuação (corretores e saldo) */}
                  {corretoresSubTab === 'pontuacao' && (
                    <>
                  <div className="mb-6">
                    <Text size="sm" color="muted">
                      Visualize o saldo e histórico de PASI Points dos corretores
                    </Text>
                  </div>

                  {/* Filtros */}
                  <div className="mb-4 bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">ID SCGP</Text>
                        <Input
                          type="text"
                          placeholder="ID SCGP do corretor..."
                          value={brokerFilterScgpId}
                          onChange={(e) => setBrokerFilterScgpId(e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Nome do Corretor</Text>
                        <Input
                          type="text"
                          placeholder="Nome do corretor..."
                          value={brokerFilterName}
                          onChange={(e) => setBrokerFilterName(e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div className="flex items-end justify-end gap-2">
                        <Button
                          variant="solid"
                          size="sm"
                          onClick={applyBrokerFilters}
                          disabled={loadingBrokers || !hasBrokerFiltersActive}
                          title={!hasBrokerFiltersActive ? 'Selecione ao menos um filtro para buscar' : undefined}
                        >
                          Buscar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearBrokerFilters}
                          disabled={loadingBrokers || (!hasSearchedBrokers && !hasBrokerFiltersActive)}
                        >
                          Limpar Filtros
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Tabela de Corretores */}
                  {!hasSearchedBrokers ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      Selecione ao menos um filtro e clique em Buscar para carregar os registros.
                    </div>
                  ) : loadingBrokers ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                          <tr>
                            <SortableTableHeader
                              sortKey="broker_id"
                              currentSort={brokerSortBy}
                              sortOrder={brokerSortOrder}
                              onSort={toggleBrokerSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Corretor
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="points_balance"
                              currentSort={brokerSortBy}
                              sortOrder={brokerSortOrder}
                              onSort={toggleBrokerSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Saldo Atual
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="total_points_earned"
                              currentSort={brokerSortBy}
                              sortOrder={brokerSortOrder}
                              onSort={toggleBrokerSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              PASI Points Ganhos
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="total_points_spent"
                              currentSort={brokerSortBy}
                              sortOrder={brokerSortOrder}
                              onSort={toggleBrokerSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              PASI Points Gastos
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="total_points_removed"
                              currentSort={brokerSortBy}
                              sortOrder={brokerSortOrder}
                              onSort={toggleBrokerSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              PASI Points Retirados
                            </SortableTableHeader>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Ações
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {sortedBrokers.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                Nenhum corretor encontrado com os filtros aplicados.
                              </td>
                            </tr>
                          ) : (
                            paginatedBrokers.map((broker) => (
                              <tr
                                key={broker.id}
                                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                onClick={() => openBrokerModal(broker)}
                              >
                                <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                                  {broker.broker?.name}
                                </td>
                                <td className="px-4 py-4 text-sm">
                                  <Text size="lg" className={`font-bold ${
                                    broker.points_balance >= 0
                                      ? 'text-green-600 dark:text-green-400'
                                      : 'text-red-600 dark:text-red-400'
                                  }`}>
                                    {broker.points_balance.toLocaleString('pt-BR')}
                                  </Text>
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                                  {broker.total_points_earned.toLocaleString('pt-BR')}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                                  {broker.total_points_spent.toLocaleString('pt-BR')}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                                  {(broker.total_points_removed ?? 0).toLocaleString('pt-BR')}
                                </td>
                                <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                                  <div className="inline-flex items-center justify-end gap-2 flex-nowrap">
                                    <PermissionGuard moduleName="portais_shop" permissionType="update" fallback={null} loadingComponent={null}>
                                      <UiTooltip content="Ajustar pontos">
                                        <button
                                          type="button"
                                          onClick={(e: React.MouseEvent) => openPointsAdjustModal(broker, e)}
                                          disabled={savingPointsAdjust}
                                          className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                          aria-label="Ajustar pontos"
                                        >
                                          <AdjustmentsHorizontalIcon className="h-4 w-4" />
                                        </button>
                                      </UiTooltip>
                                    </PermissionGuard>
                                    {broker.broker?.id_scgp != null ? (
                                      canInsertShop ? (
                                        <>
                                          <UiTooltip content="Acessar Shop">
                                            <button
                                              type="button"
                                              onClick={(e: React.MouseEvent) => handleAcessarShop(broker, e)}
                                              disabled={acessandoBrokerId === broker.id || homologandoBrokerId === broker.id}
                                              className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                              aria-label="Acessar Shop"
                                            >
                                              {acessandoBrokerId === broker.id ? (
                                                <LoadingSpinner size="sm" />
                                              ) : (
                                                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                              )}
                                            </button>
                                          </UiTooltip>
                                          <UiTooltip content="Homologação">
                                            <button
                                              type="button"
                                              onClick={(e: React.MouseEvent) => handleHomologacao(broker, e)}
                                              disabled={acessandoBrokerId === broker.id || homologandoBrokerId === broker.id}
                                              className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                              aria-label="Homologação"
                                            >
                                              {homologandoBrokerId === broker.id ? (
                                                <LoadingSpinner size="sm" />
                                              ) : (
                                                <BeakerIcon className="h-4 w-4" />
                                              )}
                                            </button>
                                          </UiTooltip>
                                        </>
                                      ) : null
                                    ) : (
                                      <Text size="xs" color="muted">Sem código SCGP</Text>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {!loadingBrokers && hasSearchedBrokers && sortedBrokers.length > 0 && brokersTotalPages > 1 && (
                    <div className="mt-4 flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
                      <Text size="sm">
                        Mostrando {brokersStartIndex + 1} a {Math.min(brokersEndIndex, sortedBrokers.length)} de {sortedBrokers.length} corretores
                      </Text>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setBrokersPage((p) => Math.max(1, p - 1))}
                          disabled={brokersPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Anterior
                        </button>
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, brokersTotalPages) }, (_, i) => {
                            let pageNumber: number
                            if (brokersTotalPages <= 5) {
                              pageNumber = i + 1
                            } else if (brokersPage <= 3) {
                              pageNumber = i + 1
                            } else if (brokersPage >= brokersTotalPages - 2) {
                              pageNumber = brokersTotalPages - 4 + i
                            } else {
                              pageNumber = brokersPage - 2 + i
                            }
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => setBrokersPage(pageNumber)}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                  brokersPage === pageNumber
                                    ? 'bg-primary-600 text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            )
                          })}
                        </div>
                        <button
                          onClick={() => setBrokersPage((p) => Math.min(brokersTotalPages, p + 1))}
                          disabled={brokersPage === brokersTotalPages}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Próximo
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Modal detalhe do corretor + favoritos */}
                  {isBrokerModalOpen && selectedBrokerForModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                          <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                            Corretor: {selectedBrokerForModal.broker?.name ?? selectedBrokerForModal.broker_id}
                          </Heading>
                          <Button variant="ghost" onClick={closeBrokerModal}>Fechar</Button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                          <div>
                            <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">Dados do corretor</Text>
                            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                              <div><span className="text-gray-500 dark:text-gray-400">ID (shop_broker):</span> <span className="text-gray-900 dark:text-white font-mono">{selectedBrokerForModal.id}</span></div>
                              <div><span className="text-gray-500 dark:text-gray-400">Broker ID:</span> <span className="text-gray-900 dark:text-white font-mono">{selectedBrokerForModal.broker_id}</span></div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">ID SCGP:</span>{' '}
                                <span className="text-gray-900 dark:text-white font-mono">
                                  {selectedBrokerForModal.broker?.id_scgp != null
                                    ? String(selectedBrokerForModal.broker.id_scgp)
                                    : '—'}
                                </span>
                              </div>
                              <div><span className="text-gray-500 dark:text-gray-400">Saldo atual:</span> <span className={`font-semibold ${selectedBrokerForModal.points_balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{selectedBrokerForModal.points_balance.toLocaleString('pt-BR')}</span></div>
                              <div><span className="text-gray-500 dark:text-gray-400">PASI Points ganhos:</span> <span className="text-gray-900 dark:text-white">{selectedBrokerForModal.total_points_earned.toLocaleString('pt-BR')}</span></div>
                              <div><span className="text-gray-500 dark:text-gray-400">PASI Points gastos:</span> <span className="text-gray-900 dark:text-white">{selectedBrokerForModal.total_points_spent.toLocaleString('pt-BR')}</span></div>
                              <div><span className="text-gray-500 dark:text-gray-400">PASI Points retirados:</span> <span className="text-gray-900 dark:text-white">{(selectedBrokerForModal.total_points_removed ?? 0).toLocaleString('pt-BR')}</span></div>
                              <div><span className="text-gray-500 dark:text-gray-400">Cadastrado em:</span> <span className="text-gray-900 dark:text-white">{formatDateTime(selectedBrokerForModal.created_at)}</span></div>
                              <div><span className="text-gray-500 dark:text-gray-400">Atualizado em:</span> <span className="text-gray-900 dark:text-white">{formatDateTime(selectedBrokerForModal.updated_at)}</span></div>
                            </div>
                          </div>
                          <div>
                            <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-2 block">Itens favoritados</Text>
                            {loadingBrokerFavorites ? (
                              <div className="flex items-center justify-center py-8">
                                <LoadingSpinner />
                              </div>
                            ) : brokerFavoriteProducts.length === 0 ? (
                              <Text size="sm" color="muted" className="py-4">Nenhum item favoritado.</Text>
                            ) : (
                              <ul className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
                                {brokerFavoriteProducts.map((fav) => (
                                  <li key={fav.product_id} className="px-4 py-3 text-sm text-gray-900 dark:text-white flex items-center justify-between">
                                    <span>{fav.product_name}</span>
                                    <span className="text-gray-500 dark:text-gray-400 font-mono text-xs">{fav.product_id}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                    </>
                  )}

                  {/* Sub-aba Extrato */}
                  {corretoresSubTab === 'historico' && (
                    <>
                  <div className="mb-6">
                    <Text size="sm" color="muted">
                      Visualize todas as transações de PASI Points realizadas
                    </Text>
                  </div>

                  {/* Filtros */}
                  <div className="mb-4 bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Tipo de Transação</Text>
                        <select
                          className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                          value={historyFilterType}
                          onChange={(e) => setHistoryFilterType(e.target.value)}
                        >
                          <option value="all">Todos</option>
                          <option value="earned">Ganhos</option>
                          <option value="spent">Gastos</option>
                          <option value="refunded">Reembolsos</option>
                          <option value="adjusted">Ajustes</option>
                        </select>
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">ID do SCGP</Text>
                        <Input
                          type="text"
                          placeholder="ID SCGP do corretor..."
                          value={historyFilterScgpId}
                          onChange={(e) => setHistoryFilterScgpId(e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Nome do corretor</Text>
                        <Input
                          type="text"
                          placeholder="Nome do corretor..."
                          value={historyFilterBrokerName}
                          onChange={(e) => setHistoryFilterBrokerName(e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Inicial</Text>
                        <Input
                          type="date"
                          value={historyDateFrom ? historyDateFrom.split('T')[0] : ''}
                          onChange={(e) => {
                            const dateValue = e.target.value
                            setHistoryDateFrom(dateValue ? `${dateValue}T00:00:00` : '')
                          }}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Final</Text>
                        <Input
                          type="date"
                          value={historyDateTo ? historyDateTo.split('T')[0] : ''}
                          onChange={(e) => {
                            const dateValue = e.target.value
                            setHistoryDateTo(dateValue ? `${dateValue}T23:59:59` : '')
                          }}
                          className="w-full"
                        />
                      </div>
                      <div className="flex items-end justify-end gap-2">
                        <Button
                          variant="solid"
                          size="sm"
                          onClick={applyHistoryFilters}
                          disabled={loadingPointsHistory || !hasHistoryFiltersActive}
                          title={!hasHistoryFiltersActive ? 'Selecione ao menos um filtro para buscar' : undefined}
                        >
                          Buscar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearHistoryFilters}
                          disabled={loadingPointsHistory || (!hasSearchedHistory && !hasHistoryFiltersActive)}
                        >
                          Limpar Filtros
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Tabela de Histórico */}
                  {!hasSearchedHistory ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      Selecione ao menos um filtro e clique em Buscar para carregar os registros.
                    </div>
                  ) : loadingPointsHistory ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                          <tr>
                            <SortableTableHeader
                              sortKey="created_at"
                              currentSort={historySortBy}
                              sortOrder={historySortOrder}
                              onSort={toggleHistorySort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Data
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="broker_id"
                              currentSort={historySortBy}
                              sortOrder={historySortOrder}
                              onSort={toggleHistorySort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Corretor
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="transaction_type"
                              currentSort={historySortBy}
                              sortOrder={historySortOrder}
                              onSort={toggleHistorySort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Tipo
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="points_amount"
                              currentSort={historySortBy}
                              sortOrder={historySortOrder}
                              onSort={toggleHistorySort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Quantidade
                            </SortableTableHeader>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                              Saldo Antes
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                              Saldo Depois
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                              Descrição
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                              Usuário
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                              Referência
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {sortedPointsHistory.length === 0 ? (
                            <tr>
                              <td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                Nenhuma transação encontrada com os filtros aplicados.
                              </td>
                            </tr>
                          ) : (
                            paginatedHistory.map((history) => (
                              <tr key={history.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                                  {formatDateTime(history.created_at)}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                                  {history.broker?.name}
                                </td>
                                <td className="px-4 py-4 text-sm">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    history.transaction_type === 'earned' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                    history.transaction_type === 'spent' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                    history.transaction_type === 'refunded' ? 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200' :
                                    'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                  }`}>
                                    {history.transaction_type === 'earned' ? 'Ganho' :
                                     history.transaction_type === 'spent' ? 'Gasto' :
                                     history.transaction_type === 'refunded' ? 'Reembolso' :
                                     'Ajuste'}
                                  </span>
                                </td>
                                <td className="px-4 py-4 text-sm">
                                  <Text size="sm" className={`font-bold ${
                                    history.points_amount >= 0
                                      ? 'text-green-600 dark:text-green-400'
                                      : 'text-red-600 dark:text-red-400'
                                  }`}>
                                    {history.points_amount >= 0 ? '+' : ''}{history.points_amount.toLocaleString('pt-BR')}
                                  </Text>
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                                  {history.points_balance_before.toLocaleString('pt-BR')}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                                  {history.points_balance_after.toLocaleString('pt-BR')}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                                  {history.description || '-'}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                                  {history.created_by_user?.name || '-'}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                                  {history.order_number || '-'}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {!loadingPointsHistory && hasSearchedHistory && sortedPointsHistory.length > 0 && historyTotalPages > 1 && (
                    <div className="mt-4 flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
                      <Text size="sm">
                        Mostrando {historyStartIndex + 1} a {Math.min(historyEndIndex, sortedPointsHistory.length)} de {sortedPointsHistory.length} transações
                      </Text>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                          disabled={historyPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Anterior
                        </button>
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, historyTotalPages) }, (_, i) => {
                            let pageNumber: number
                            if (historyTotalPages <= 5) {
                              pageNumber = i + 1
                            } else if (historyPage <= 3) {
                              pageNumber = i + 1
                            } else if (historyPage >= historyTotalPages - 2) {
                              pageNumber = historyTotalPages - 4 + i
                            } else {
                              pageNumber = historyPage - 2 + i
                            }
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => setHistoryPage(pageNumber)}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                  historyPage === pageNumber
                                    ? 'bg-primary-600 text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            )
                          })}
                        </div>
                        <button
                          onClick={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))}
                          disabled={historyPage === historyTotalPages}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Próximo
                        </button>
                      </div>
                    </div>
                  )}
                    </>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'indicadores' && (
              <div className="flex-1 flex flex-col overflow-y-auto">
                <div className="py-6 px-4 sm:px-6 lg:px-8">
                  <div className="mb-6">
                    <Text size="sm" color="muted">
                      Visualize indicadores e métricas importantes sobre vendas e pedidos
                    </Text>
                  </div>

                  {/* Filtros de Data */}
                  <div className="mb-4 bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Inicial</Text>
                        <Input
                          type="date"
                          value={indicatorsDateFrom ? indicatorsDateFrom.split('T')[0] : ''}
                          onChange={(e) => {
                            const dateValue = e.target.value
                            setIndicatorsDateFrom(dateValue ? `${dateValue}T00:00:00` : '')
                          }}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Final</Text>
                        <Input
                          type="date"
                          value={indicatorsDateTo ? indicatorsDateTo.split('T')[0] : ''}
                          onChange={(e) => {
                            const dateValue = e.target.value
                            setIndicatorsDateTo(dateValue ? `${dateValue}T23:59:59` : '')
                          }}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">ID SCGP</Text>
                        <Input
                          type="text"
                          placeholder="ID SCGP do corretor..."
                          value={indicatorsFilterScgpId}
                          onChange={(e) => setIndicatorsFilterScgpId(e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Nome do Corretor</Text>
                        <Input
                          type="text"
                          placeholder="Nome do corretor..."
                          value={indicatorsFilterBrokerName}
                          onChange={(e) => setIndicatorsFilterBrokerName(e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div className="flex items-end justify-end">
                        <Button variant="outline" onClick={clearIndicatorsFilters}>
                          Limpar Filtros
                        </Button>
                      </div>
                    </div>
                  </div>

                  {loadingIndicators ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <>
                      {/* Cards de KPIs */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
                        <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg p-5 text-white">
                          <div className="text-xs opacity-80 mb-1">Total de Pedidos</div>
                          <div className="text-2xl font-semibold">{metrics.total_orders.toLocaleString('pt-BR')}</div>
                        </div>
                        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg p-5 text-white">
                          <div className="text-xs opacity-80 mb-1">Total de PASI Points disponibilizados</div>
                          <div className="text-2xl font-semibold">{metrics.total_points_disponibilizados.toLocaleString('pt-BR')}</div>
                        </div>
                        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-5 text-white">
                          <div className="text-xs opacity-80 mb-1">PASI Points Gastos</div>
                          <div className="text-2xl font-semibold">{metrics.total_points_spent.toLocaleString('pt-BR')}</div>
                        </div>
                        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-5 text-white">
                          <div className="text-xs opacity-80 mb-1">Produtos Vendidos</div>
                          <div className="text-2xl font-semibold">{metrics.total_products_sold.toLocaleString('pt-BR')}</div>
                        </div>
                        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-5 text-white">
                          <div className="text-xs opacity-80 mb-1">Ticket Médio (PASI Points)</div>
                          <div className="text-2xl font-semibold">{Math.round(metrics.average_order_value).toLocaleString('pt-BR')}</div>
                        </div>
                        <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg p-5 text-white">
                          <div className="text-xs opacity-80 mb-1">Corretores com pedidos</div>
                          <div className="text-2xl font-semibold">{metrics.total_brokers}</div>
                        </div>
                      </div>

                      {/* Gráficos em linhas: Pedidos por Mês ocupa a largura total */}
                      <div className="space-y-6 mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Produtos Mais Populares */}
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 min-w-0">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Produtos Mais Populares
                          </Heading>
                          <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={topProducts.slice(0, 5)}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis 
                                  dataKey="product_name" 
                                  stroke="#6b7280"
                                  angle={-45}
                                  textAnchor="end"
                                  height={100}
                                  interval={0}
                                />
                                <YAxis stroke="#6b7280" />
                                <Tooltip 
                                  formatter={(value) => `${Number(value).toLocaleString('pt-BR')} unidades`}
                                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}
                                />
                                <Bar dataKey="total_quantity" fill="#008B95" radius={[4, 4, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        {/* Pedidos por Status */}
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 min-w-0">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Pedidos por Status
                          </Heading>
                          <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={ordersByStatus.map(s => ({
                                    name: s.status === 'pending' ? 'Pendente' :
                                          s.status === 'confirmed' ? 'Confirmado' :
                                          s.status === 'shipped' ? 'Enviado' :
                                          s.status === 'delivered' ? 'Entregue' :
                                          s.status === 'cancelled' ? 'Cancelado' : s.status,
                                    value: s.count
                                  }))}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ name, percent }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
                                  outerRadius={100}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  {ordersByStatus.map((entry, index) => {
                                    const colors = ['#008B95', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']
                                    return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                                  })}
                                </Pie>
                                <Tooltip />
                                <Legend />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                        </div>

                        <div className="space-y-6 min-w-0">
                        {/* Pedidos por Mês — linha inteira */}
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 min-w-0">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Pedidos por Mês
                          </Heading>
                          <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={ordersByMonth}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="month" stroke="#6b7280" />
                                <YAxis stroke="#6b7280" />
                                <Tooltip 
                                  formatter={(value) => Number(value).toLocaleString('pt-BR')}
                                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}
                                />
                                <Legend />
                                <Line 
                                  type="monotone" 
                                  dataKey="count" 
                                  name="Quantidade de Pedidos"
                                  stroke="#008B95" 
                                  strokeWidth={2}
                                  dot={{ fill: '#008B95', r: 4 }}
                                />
                                <Line 
                                  type="monotone" 
                                  dataKey="total_points" 
                                  name="PASI Points Gastos"
                                  stroke="#10b981" 
                                  strokeWidth={2}
                                  dot={{ fill: '#10b981', r: 4 }}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Total de frete por mês */}
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 min-w-0">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Total de frete por mês
                          </Heading>
                          <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={freightByMonth}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="month" stroke="#6b7280" />
                                <YAxis stroke="#6b7280" />
                                <Tooltip
                                  formatter={(value) =>
                                    new Intl.NumberFormat('pt-BR', {
                                      style: 'currency',
                                      currency: 'BRL',
                                    }).format(Number(value))
                                  }
                                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}
                                />
                                <Legend />
                                <Bar
                                  dataKey="total_freight"
                                  name="Total frete"
                                  fill="#f97316"
                                  radius={[4, 4, 0, 0]}
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        {/* Tempo médio de entrega por mês */}
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 min-w-0">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-1">
                            Tempo Médio de Entrega
                          </Heading>
                          <Text size="xs" color="muted" className="mb-4 block">
                            Média de dias do pedido até a entrega, por mês de conclusão.
                          </Text>
                          <div className="h-80">
                            {deliveryLeadByMonth.length === 0 ? (
                              <div className="h-full flex items-center justify-center rounded-lg border border-dashed border-gray-200 dark:border-gray-600">
                                <Text size="sm" color="muted">
                                  Nenhum pedido entregue no período.
                                </Text>
                              </div>
                            ) : (
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={deliveryLeadByMonth}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                  <XAxis dataKey="month" stroke="#6b7280" />
                                  <YAxis
                                    stroke="#6b7280"
                                    allowDecimals
                                    tickFormatter={(v) =>
                                      Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 1 })
                                    }
                                    label={{
                                      value: 'Dias (média)',
                                      angle: -90,
                                      position: 'insideLeft',
                                      style: { textAnchor: 'middle', fill: '#6b7280', fontSize: 12 },
                                    }}
                                  />
                                  <Tooltip
                                    formatter={(value) => [
                                      `${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} dias`,
                                      'Média até entrega',
                                    ]}
                                    labelFormatter={(label) => `Entregues em ${label}`}
                                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}
                                  />
                                  <Legend
                                    verticalAlign="bottom"
                                    formatter={() => 'Média de dias do pedido até a entrega'}
                                  />
                                  <Bar
                                    dataKey="avg_delivery_days"
                                    name="Média até entrega"
                                    fill="#6366f1"
                                    radius={[4, 4, 0, 0]}
                                  />
                                </BarChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>
                        </div>
                      </div>

                      {/* Rankings (abas) */}
                      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                          Rankings
                        </Heading>
                        <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
                          <nav className="flex flex-wrap gap-x-6" aria-label="Rankings">
                            <button
                              type="button"
                              onClick={() => setIndicatorsRankingTab('produtos')}
                              className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                                indicatorsRankingTab === 'produtos'
                                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                              }`}
                            >
                              Produtos
                            </button>
                            <button
                              type="button"
                              onClick={() => setIndicatorsRankingTab('corretores')}
                              className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                                indicatorsRankingTab === 'corretores'
                                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                              }`}
                            >
                              Corretores
                            </button>
                            <button
                              type="button"
                              onClick={() => setIndicatorsRankingTab('favoritos')}
                              className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                                indicatorsRankingTab === 'favoritos'
                                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                              }`}
                            >
                              Mais favoritados
                            </button>
                          </nav>
                        </div>

                        {indicatorsRankingTab === 'produtos' && (
                          <div className="flex flex-col">
                            {topProducts.length === 0 ? (
                              <div className="min-h-[20rem] flex items-center justify-center rounded-lg border border-dashed border-gray-200 dark:border-gray-600">
                                <Text size="sm" color="muted">
                                  Nenhum produto encontrado.
                                </Text>
                              </div>
                            ) : (
                              <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                {topProducts.map((product, index) => (
                                  <li
                                    key={product.product_id}
                                    className="flex items-center gap-3 px-4 py-3 bg-gray-50/50 dark:bg-gray-900/20 hover:bg-gray-100/80 dark:hover:bg-gray-700/40"
                                  >
                                    <span className="flex-shrink-0 flex h-9 w-9 items-center justify-center rounded-full bg-pink-500 text-sm font-semibold text-white">
                                      {index + 1}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {product.product_name}
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {product.total_quantity.toLocaleString('pt-BR')} unidades ·{' '}
                                        {product.total_points.toLocaleString('pt-BR')} PASI Points
                                      </div>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}

                        {indicatorsRankingTab === 'corretores' && (
                          <div className="flex flex-col">
                            {topBrokers.length === 0 ? (
                              <div className="min-h-[20rem] flex items-center justify-center rounded-lg border border-dashed border-gray-200 dark:border-gray-600">
                                <Text size="sm" color="muted">
                                  Nenhum corretor encontrado.
                                </Text>
                              </div>
                            ) : (
                              <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                {topBrokers.map((broker, index) => (
                                  <li
                                    key={broker.broker_id}
                                    className="flex items-center gap-3 px-4 py-3 bg-gray-50/50 dark:bg-gray-900/20 hover:bg-gray-100/80 dark:hover:bg-gray-700/40"
                                  >
                                    <span className="flex-shrink-0 flex h-9 w-9 items-center justify-center rounded-full bg-pink-500 text-sm font-semibold text-white">
                                      {index + 1}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {broker.broker_name || '—'}
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {broker.total_orders.toLocaleString('pt-BR')} pedidos ·{' '}
                                        {broker.total_points.toLocaleString('pt-BR')} PASI Points
                                      </div>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}

                        {indicatorsRankingTab === 'favoritos' && (
                          <div className="flex flex-col">
                            {topFavoriteProducts.length === 0 ? (
                              <div className="min-h-[20rem] flex items-center justify-center rounded-lg border border-dashed border-gray-200 dark:border-gray-600">
                                <Text size="sm" color="muted">
                                  Nenhum produto favoritado encontrado.
                                </Text>
                              </div>
                            ) : (
                              <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                {topFavoriteProducts.map((product, index) => (
                                  <li
                                    key={product.product_id}
                                    className="flex items-center gap-3 px-4 py-3 bg-gray-50/50 dark:bg-gray-900/20 hover:bg-gray-100/80 dark:hover:bg-gray-700/40"
                                  >
                                    <span className="flex-shrink-0 flex h-9 w-9 items-center justify-center rounded-full bg-pink-500 text-sm font-semibold text-white">
                                      {index + 1}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {product.product_name}
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {product.favorite_count.toLocaleString('pt-BR')}{' '}
                                        {product.favorite_count === 1 ? 'favorito' : 'favoritos'}
                                      </div>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'historico' && (
              <>
            {/* Filtros */}
            <div className="px-6 py-4">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="mb-4">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Filtros</Heading>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Corretor</Text>
                    <Input
                      type="text"
                      placeholder="Digite o nome do corretor..."
                      value={shopCorretorQuery}
                      onChange={(e) => setShopCorretorQuery(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Inicial</Text>
                    <Input
                      type="date"
                      value={shopDateFrom ? shopDateFrom.split('T')[0] : ''}
                      onChange={(e) => {
                        const dateValue = e.target.value
                        setShopDateFrom(dateValue ? `${dateValue}T00:00:00` : '')
                      }}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Final</Text>
                    <Input
                      type="date"
                      value={shopDateTo ? shopDateTo.split('T')[0] : ''}
                      onChange={(e) => {
                        const dateValue = e.target.value
                        setShopDateTo(dateValue ? `${dateValue}T23:59:59` : '')
                      }}
                      className="w-full"
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShopCorretorQuery('')
                      setShopDateFrom('')
                      setShopDateTo('')
                    }}
                  >
                    Limpar Filtros
                  </Button>
                </div>
              </div>
            </div>

            {/* Sub-abas: Lista / Gráficos */}
            <div className="px-6 border-b border-gray-200 dark:border-gray-700">
              <nav className="flex space-x-8" aria-label="Visualização do histórico">
                <button
                  type="button"
                  onClick={() => setShopHistoricoView('lista')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm ${
                    shopHistoricoView === 'lista'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Lista
                </button>
                <button
                  type="button"
                  onClick={() => setShopHistoricoView('graficos')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm ${
                    shopHistoricoView === 'graficos'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Gráficos
                </button>
              </nav>
            </div>

            {shopHistoricoView === 'lista' && (
              <>
            {/* Tabela */}
            <div className="flex-1 px-6 py-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">Histórico de Acessos</Heading>
                  {!loadingShopHistorico && shopFiltrados.length > 0 && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => scrollShopTableBy(-320)}
                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                        aria-label="Rolar para a esquerda"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => scrollShopTableBy(320)}
                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                        aria-label="Rolar para a direita"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex-1 overflow-auto">
                  {loadingShopHistorico ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div ref={shopTableScrollRef} className="overflow-x-auto" onWheel={handleShopTableWheel}>
                      <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 min-w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                          <tr>
                            <SortableTableHeader
                              sortKey="corretor"
                              currentSort={shopSortBy}
                              sortOrder={shopSortOrder}
                              onSort={setShopSortBy}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Corretor
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="access_timestamp"
                              currentSort={shopSortBy}
                              sortOrder={shopSortOrder}
                              onSort={setShopSortBy}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Data/Hora Acesso
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="user_agent"
                              currentSort={shopSortBy}
                              sortOrder={shopSortOrder}
                              onSort={setShopSortBy}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              User Agent
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="ip_address"
                              currentSort={shopSortBy}
                              sortOrder={shopSortOrder}
                              onSort={setShopSortBy}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              IP
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="token"
                              currentSort={shopSortBy}
                              sortOrder={shopSortOrder}
                              onSort={setShopSortBy}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Token
                            </SortableTableHeader>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {shopFiltradosPaginados.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="px-4 py-8 text-center">
                                <Text size="sm" color="muted">Nenhum dado disponível.</Text>
                              </td>
                            </tr>
                          ) : (
                            shopFiltradosPaginados.map((r) => (
                              <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-4 py-4 text-left">
                                  <div className="flex flex-col">
                                    <Text size="sm" className="text-gray-900 dark:text-white font-medium">{r.corretor || '-'}</Text>
                                    {r.cnpj && (
                                      <Text size="xs" className="text-gray-500 dark:text-gray-400">{r.cnpj}</Text>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-500 dark:text-gray-400">
                                    {formatDateTime(r.accessed_at)}
                                  </Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text
                                    size="xs"
                                    className="text-gray-500 dark:text-gray-400 break-all max-w-xs"
                                    title={r.user_agent || ''}
                                  >
                                    {r.user_agent
                                      ? r.user_agent.length > 50
                                        ? `${r.user_agent.substring(0, 50)}...`
                                        : r.user_agent
                                      : '-'}
                                  </Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text
                                    size="xs"
                                    className="text-gray-500 dark:text-gray-400 break-all max-w-xs"
                                  >
                                    {r.ip_address || '-'}
                                  </Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text
                                    size="xs"
                                    className="text-gray-500 dark:text-gray-400 break-all max-w-xs font-mono"
                                  >
                                    {r.token || '-'}
                                  </Text>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                {shopTotalPages > 1 && !loadingShopHistorico && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <Text size="sm">
                          Mostrando {shopStartIndex + 1} a {Math.min(shopEndIndex, shopFiltrados.length)} de {shopFiltrados.length} registros
                        </Text>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setShopCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={shopCurrentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Anterior
                        </button>
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, shopTotalPages) }, (_, i) => {
                            let pageNumber: number
                            if (shopTotalPages <= 5) {
                              pageNumber = i + 1
                            } else if (shopCurrentPage <= 3) {
                              pageNumber = i + 1
                            } else if (shopCurrentPage >= shopTotalPages - 2) {
                              pageNumber = shopTotalPages - 4 + i
                            } else {
                              pageNumber = shopCurrentPage - 2 + i
                            }
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => setShopCurrentPage(pageNumber)}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                  shopCurrentPage === pageNumber
                                    ? 'bg-primary-600 text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            )
                          })}
                        </div>
                        <button
                          onClick={() => setShopCurrentPage((p) => Math.min(shopTotalPages, p + 1))}
                          disabled={shopCurrentPage === shopTotalPages}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Próximo
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
              </>
            )}

            {shopHistoricoView === 'graficos' && (
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">{shopTotalHistorico}</Text>
                      <Text size="sm" className="text-white">Acessos - Histórico</Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">{shopAcessosHoje}</Text>
                      <Text size="sm" className="text-white">Acessos Hoje</Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-violet-500 to-violet-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">{shopCorretoresComAcesso}</Text>
                      <Text size="sm" className="text-white">Corretores que já acessaram</Text>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-slate-600 to-slate-700 shadow-lg rounded-lg p-6 text-white">
                  <Text size="sm" className="text-white/90 font-semibold mb-3 block text-center md:text-left">
                    Top 3 corretores com mais acessos
                  </Text>
                  {loadingShopHistorico ? (
                    <div className="flex justify-center py-2">
                      <LoadingSpinner />
                    </div>
                  ) : shopTopCorretoresAcesso.length === 0 ? (
                    <Text size="sm" className="text-white/90 text-center">
                      Nenhum acesso vinculado a corretor no histórico.
                    </Text>
                  ) : (
                    <ol className="space-y-3 list-decimal list-inside">
                      {shopTopCorretoresAcesso.map((c) => (
                        <li key={c.broker_id} className="text-sm marker:font-bold">
                          <span className="inline-flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="font-medium">{truncateShopCorretorName(c.nome, 48)}</span>
                            {c.id_scgp != null && (
                              <span className="text-white/80 text-xs">SCGP {c.id_scgp}</span>
                            )}
                            <span className="font-bold tabular-nums">{c.acessos} acessos</span>
                          </span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>

                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                    Acessos por data
                  </Heading>
                  {!shopDateFrom && !shopDateTo && shopHistoricoAcessos.length > 0 && (
                    <Text size="sm" color="muted" className="mb-4 block">
                      {`Exibindo últimos ${HISTORICO_CHART_MAX_DAYS} dias quando o histórico é longo`}
                    </Text>
                  )}

                  {loadingShopHistorico ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : shopAcessosPorData.length === 0 || shopAcessosPorData.every((d) => d.total === 0) ? (
                    <div className="flex items-center justify-center py-12">
                      <Text size="sm" color="muted">
                        Nenhum acesso no período selecionado.
                      </Text>
                    </div>
                  ) : (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={shopAcessosPorData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis
                            dataKey="date"
                            stroke="#6b7280"
                            tickFormatter={formatShopChartDateTick}
                            interval="preserveStartEnd"
                            minTickGap={24}
                          />
                          <YAxis stroke="#6b7280" allowDecimals={false} />
                          <Tooltip
                            formatter={(value) => [`${Number(value).toLocaleString('pt-BR')} acessos`, 'Total']}
                            labelFormatter={(label) => `Data: ${formatDateShort(String(label))}`}
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}
                          />
                          <Line
                            type="monotone"
                            dataKey="total"
                            stroke="#008B95"
                            strokeWidth={2}
                            dot={{ fill: '#008B95', r: 3 }}
                            activeDot={{ r: 5 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                    Corretores por data de primeiro acesso
                  </Heading>
                  {!shopDateFrom && !shopDateTo && shopHistoricoAcessos.length > 0 && (
                    <Text size="sm" color="muted" className="mb-4 block">
                      {`Exibindo últimos ${HISTORICO_CHART_MAX_DAYS} dias quando o histórico é longo`}
                    </Text>
                  )}

                  {loadingShopHistorico ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : shopCorretoresPrimeiroAcessoPorData.length === 0 ||
                    shopCorretoresPrimeiroAcessoPorData.every((d) => d.total === 0) ? (
                    <div className="flex items-center justify-center py-12">
                      <Text size="sm" color="muted">
                        Nenhum corretor com primeiro acesso no período selecionado.
                      </Text>
                    </div>
                  ) : (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={shopCorretoresPrimeiroAcessoPorData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis
                            dataKey="date"
                            stroke="#6b7280"
                            tickFormatter={formatShopChartDateTick}
                            interval="preserveStartEnd"
                            minTickGap={24}
                          />
                          <YAxis stroke="#6b7280" allowDecimals={false} />
                          <Tooltip
                            formatter={(value) => [
                              `${Number(value).toLocaleString('pt-BR')} corretores`,
                              'Primeiro acesso',
                            ]}
                            labelFormatter={(label) => `Data: ${formatDateShort(String(label))}`}
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}
                          />
                          <Line
                            type="monotone"
                            dataKey="total"
                            stroke="#7c3aed"
                            strokeWidth={2}
                            dot={{ fill: '#7c3aed', r: 3 }}
                            activeDot={{ r: 5 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
            )}
              </>
            )}

            {activeTab === 'parametros' && (
              <div className="flex-1 flex flex-col overflow-y-auto">
                <div className="py-6 px-4 sm:px-6 lg:px-8">
                  <div className="mb-6">
                    <Text size="sm" color="muted">
                      Configurações do shop. Edite o valor conforme necessário.
                    </Text>
                  </div>

                  {loadingParameters ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900/30">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                            <th className="px-4 py-2"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {parameters.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                Nenhum parâmetro cadastrado.
                              </td>
                            </tr>
                          ) : (
                            parameters.map((param) => (
                              <tr key={param.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 max-w-md truncate" title={param.description ?? undefined}>
                                  {param.description ?? '—'}
                                </td>
                                <td
                                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300"
                                  title={String(param.value).length > 20 ? String(param.value) : undefined}
                                >
                                  {String(param.value).length > 20
                                    ? `${String(param.value).slice(0, 20)}…`
                                    : param.value}
                                </td>
                                <td className="px-4 py-2 text-right">
                                  {canUpdateShop && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setEditingParameter(param)
                                        setParameterEditValue(param.value)
                                        const hasHtml = typeof param.value === 'string' && param.value.includes('<')
                                        setParameterEditMode(hasHtml ? 'html' : 'simple')
                                        setIsParameterModalOpen(true)
                                      }}
                                    >
                                      Editar
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Modal de Produto */}
            {isProductModalOpen && editingProduct && (
              <ProductModal
                product={editingProduct}
                categories={categories}
                canInsert={canInsertShop}
                canUpdate={canUpdateShop}
                onClose={() => {
                  setIsProductModalOpen(false)
                  setEditingProduct(null)
                }}
                onSave={async () => {
                  // O modal já faz o save internamente
                  await loadProducts()
                  setIsProductModalOpen(false)
                  setEditingProduct(null)
                }}
              />
            )}

            {/* Modal de Categoria */}
            {isCategoryModalOpen && editingCategory && (
              <CategoryModal
                category={editingCategory}
                onClose={() => {
                  setIsCategoryModalOpen(false)
                  setEditingCategory(null)
                }}
                onSave={async () => {
                  // O modal já faz o save internamente
                  await loadCategories()
                  setIsCategoryModalOpen(false)
                  setEditingCategory(null)
                }}
              />
            )}

            {/* Modal de Edição de Parâmetro */}
            {isParameterModalOpen && editingParameter && (
              <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                  <div
                    className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                    onClick={() => {
                      setIsParameterModalOpen(false)
                      setEditingParameter(null)
                      setParameterEditMode('simple')
                    }}
                  />
                  <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full" onClick={(e) => e.stopPropagation()}>
                    <div className="px-4 pt-5 pb-4 sm:p-6">
                      <div className="flex items-center justify-between mb-4">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                          Editar parâmetro
                        </Heading>
                        <button
                          type="button"
                          onClick={() => {
                            setIsParameterModalOpen(false)
                            setEditingParameter(null)
                            setParameterEditMode('simple')
                          }}
                          className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                        >
                          <span className="sr-only">Fechar</span>
                          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-4 block font-mono">
                        {editingParameter.key}
                      </Text>
                      <div className="space-y-4">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Text size="sm" className="text-gray-700 dark:text-gray-300 block">
                              Valor *
                            </Text>
                            <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 overflow-hidden text-xs">
                              <button
                                type="button"
                                className={`px-2 py-1 ${parameterEditMode === 'simple' ? 'bg-primary-600 text-white' : 'text-gray-700 dark:text-gray-300'}`}
                                onClick={() => setParameterEditMode('simple')}
                                disabled={!canUpdateShop}
                              >
                                Valor simples
                              </button>
                              <button
                                type="button"
                                className={`px-2 py-1 border-l border-gray-300 dark:border-gray-600 ${parameterEditMode === 'html' ? 'bg-primary-600 text-white' : 'text-gray-700 dark:text-gray-300'}`}
                                onClick={() => setParameterEditMode('html')}
                                disabled={!canUpdateShop}
                              >
                                Descritivo HTML
                              </button>
                            </div>
                          </div>
                          {parameterEditMode === 'simple' ? (
                            <Input
                              type="text"
                              value={parameterEditValue}
                              onChange={(e) => setParameterEditValue(e.target.value)}
                              className="w-full"
                              disabled={!canUpdateShop}
                            />
                          ) : (
                            <div className="w-full text-sm text-gray-600 dark:text-gray-400">
                              <Editor
                                value={parameterEditValue}
                                onChange={(value) => {
                                  if (!canUpdateShop) return
                                  setParameterEditValue(value)
                                }}
                                placeholder="Digite o conteúdo em HTML (parágrafos, listas, etc.)"
                                disabled={!canUpdateShop}
                                stickyToolbar
                              />
                            </div>
                          )}
                          <Text size="xs" className="mt-1 text-gray-500 dark:text-gray-400">
                            Use "Valor simples" para textos ou números diretos, ou "Descritivo HTML" para conteúdo formatado com marcadores HTML.
                          </Text>
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Descrição</Text>
                          <div className="w-full rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
                            {editingParameter.description ?? '—'}
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 flex justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => { setIsParameterModalOpen(false); setEditingParameter(null) }}
                        >
                          Cancelar
                        </Button>
                        {canUpdateShop && (
                          <Button
                            variant="solid"
                            disabled={savingParameter || !parameterEditValue.trim()}
                            onClick={async () => {
                              if (!editingParameter?.id || !parameterEditValue.trim()) return
                              setSavingParameter(true)
                              const oldValue = editingParameter.value
                              const newValue = parameterEditValue.trim()
                              try {
                                await shopService.updateParameter(editingParameter.id, {
                                  value: newValue,
                                })
                                await userLogsService.logUpdate(
                                  'parameter',
                                  editingParameter.id,
                                  `Alterou parâmetro no Shop: ${editingParameter.key}`,
                                  { value: oldValue },
                                  { value: newValue },
                                  { parameter_key: editingParameter.key, context: 'shop' }
                                )
                                await loadParameters()
                                setIsParameterModalOpen(false)
                                setEditingParameter(null)
                              } catch (err: any) {
                                console.error('Erro ao salvar parâmetro:', err)
                                alert(err?.message || 'Erro ao salvar parâmetro. Tente novamente.')
                              } finally {
                                setSavingParameter(false)
                              }
                            }}
                          >
                            {savingParameter ? 'Salvando...' : 'Salvar'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de Movimentação de Estoque */}
            {isStockModalOpen && stockModalProduct && (
              <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                  <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => { setIsStockModalOpen(false); setStockModalProduct(null) }} />
                  <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full" onClick={(e) => e.stopPropagation()}>
                    <div className="px-4 pt-5 pb-4 sm:p-6">
                      <div className="flex items-center justify-between mb-4">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                          Movimentar estoque
                        </Heading>
                        <button
                          type="button"
                          onClick={() => { setIsStockModalOpen(false); setStockModalProduct(null) }}
                          className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                        >
                          <span className="sr-only">Fechar</span>
                          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-4 block">
                        Produto: <strong>{stockModalProduct.name}</strong> - Estoque atual: <strong>{stockModalProduct.stock_quantity ?? 0}</strong>
                      </Text>
                      <div className="space-y-4">
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Tipo *</Text>
                          <select
                            value={stockMovementType}
                            onChange={(e) => setStockMovementType(e.target.value as ShopStockMovementType)}
                            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                          >
                            <option value="in">Entrada</option>
                            <option value="out">Saída</option>
                            <option value="adjustment">Ajuste (definir novo valor)</option>
                          </select>
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            {stockMovementType === 'adjustment' ? 'Novo valor de estoque *' : 'Quantidade *'}
                          </Text>
                          <Input
                            type="number"
                            min={stockMovementType === 'adjustment' ? 0 : 1}
                            value={stockMovementQuantity}
                            onChange={(e) => setStockMovementQuantity(parseInt(e.target.value, 10) || 0)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Descrição *</Text>
                          <textarea
                            value={stockMovementDescription}
                            onChange={(e) => setStockMovementDescription(e.target.value)}
                            rows={2}
                            required
                            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                            placeholder="Informe o motivo da movimentação"
                          />
                        </div>
                      </div>
                      <div className="mt-6 flex justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => { setIsStockModalOpen(false); setStockModalProduct(null) }}
                        >
                          Cancelar
                        </Button>
                        <Button
                          variant="solid"
                          disabled={!canUpdateShop || savingStockMovement || (stockMovementType !== 'adjustment' && stockMovementQuantity < 1) || (stockMovementType === 'adjustment' && stockMovementQuantity < 0) || !stockMovementDescription.trim()}
                          onClick={async () => {
                            if (!canUpdateShop) return
                            if (!stockModalProduct?.id) return
                            const qty = stockMovementType === 'adjustment' ? stockMovementQuantity : stockMovementQuantity
                            if (stockMovementType !== 'adjustment' && qty < 1) return
                            if (!stockMovementDescription.trim()) {
                              alert('Informe a descrição da movimentação.')
                              return
                            }
                            setSavingStockMovement(true)
                            try {
                              await shopService.registerStockMovement({
                                product_id: stockModalProduct.id,
                                movement_type: stockMovementType,
                                quantity: qty,
                                description: stockMovementDescription.trim(),
                                created_by: user?.id ?? null,
                                reference_type: 'manual',
                                reference_id: null,
                              })
                              await loadProducts()
                              await loadStockMovements()
                              setIsStockModalOpen(false)
                              setStockModalProduct(null)
                            } catch (err: any) {
                              console.error('Erro ao registrar movimentação:', err)
                              alert(err?.message || 'Erro ao registrar movimentação. Tente novamente.')
                            } finally {
                              setSavingStockMovement(false)
                            }
                          }}
                        >
                          {savingStockMovement ? 'Salvando...' : 'Salvar'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de ajuste manual de pontos */}
            {isPointsAdjustModalOpen && pointsAdjustBroker && canUpdateShop && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                onClick={closePointsAdjustModal}
              >
                <div
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0">
                    <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                      Ajustar pontos
                    </Heading>
                    <Button variant="ghost" onClick={closePointsAdjustModal} disabled={savingPointsAdjust}>
                      Fechar
                    </Button>
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Esquerda: corretor + calculadora */}
                      <div className="space-y-4">
                        <div>
                          <Text size="xs" className="text-gray-500 dark:text-gray-400 mb-1 block uppercase tracking-wide">
                            Corretor
                          </Text>
                          <Text size="lg" className="text-gray-900 dark:text-white font-semibold block">
                            {pointsAdjustBroker.broker?.name ?? pointsAdjustBroker.broker_id}
                          </Text>
                        </div>
                        <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 p-4 space-y-2 text-sm">
                          <Text size="xs" className="text-gray-500 dark:text-gray-400 mb-2 block uppercase tracking-wide">
                            Calculadora
                          </Text>
                          <div className="flex justify-between gap-4">
                            <span className="text-gray-500 dark:text-gray-400">Saldo atual</span>
                            <span className={`font-semibold ${pointsAdjustBroker.points_balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                              {pointsAdjustBroker.points_balance.toLocaleString('pt-BR')}
                            </span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span className="text-gray-500 dark:text-gray-400">Ajuste</span>
                            <span className={`font-semibold ${pointsAdjustDelta >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                              {pointsAdjustDelta > 0 ? '+' : ''}{pointsAdjustDelta.toLocaleString('pt-BR')}
                            </span>
                          </div>
                          <div className="flex justify-between gap-4 border-t border-gray-200 dark:border-gray-700 pt-2">
                            <span className="text-gray-700 dark:text-gray-300 font-medium">Saldo resultante</span>
                            <span className={`font-bold text-lg ${pointsAdjustProjectedBalance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                              {pointsAdjustProjectedBalance.toLocaleString('pt-BR')}
                            </span>
                          </div>
                          {pointsAdjustProjectedBalance < 0 && (
                            <Text size="xs" className="text-red-600 dark:text-red-400 block">
                              O saldo não pode ficar negativo. Reduza a quantidade a retirar.
                            </Text>
                          )}
                        </div>
                      </div>

                      {/* Direita: campos */}
                      <div className="space-y-4">
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Operação *</Text>
                          <div
                            className="flex flex-wrap gap-4 sm:gap-6"
                            role="radiogroup"
                            aria-label="Operação"
                          >
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="points-adjust-operation"
                                value="add"
                                checked={pointsAdjustOperation === 'add'}
                                onChange={() => setPointsAdjustOperation('add')}
                                className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-800 dark:text-gray-200 font-medium">
                                Adicionar pontos
                              </span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="points-adjust-operation"
                                value="remove"
                                checked={pointsAdjustOperation === 'remove'}
                                onChange={() => setPointsAdjustOperation('remove')}
                                className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-800 dark:text-gray-200 font-medium">
                                Retirar pontos
                              </span>
                            </label>
                          </div>
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Quantidade *</Text>
                          <Input
                            type="number"
                            min={1}
                            step={1}
                            value={pointsAdjustQuantity}
                            onChange={(e) => setPointsAdjustQuantity(parseInt(e.target.value, 10) || 0)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Observação *</Text>
                          <textarea
                            value={pointsAdjustObservation}
                            onChange={(e) => setPointsAdjustObservation(e.target.value)}
                            rows={4}
                            required
                            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                            placeholder="Informe o motivo do ajuste"
                          />
                          <div className="mt-2 rounded-md border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 px-3 py-2">
                            <Text size="xs" className="text-red-700 dark:text-red-300 font-bold">
                              ATENÇÃO: Esta observação aparecerá para o corretor no extrato do PASI Shop.
                            </Text>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
                    <Button variant="outline" onClick={closePointsAdjustModal} disabled={savingPointsAdjust}>
                      Cancelar
                    </Button>
                    <Button
                      variant="solid"
                      disabled={
                        !canUpdateShop ||
                        savingPointsAdjust ||
                        pointsAdjustDelta === 0 ||
                        pointsAdjustProjectedBalance < 0 ||
                        !pointsAdjustObservation.trim()
                      }
                      onClick={async () => {
                        if (!canUpdateShop || !pointsAdjustBroker) return
                        if (pointsAdjustDelta === 0) {
                          alert('Informe uma quantidade maior que zero.')
                          return
                        }
                        if (pointsAdjustProjectedBalance < 0) {
                          alert('O saldo não pode ficar negativo.')
                          return
                        }
                        if (!pointsAdjustObservation.trim()) {
                          alert('Informe a observação do ajuste.')
                          return
                        }
                        const brokerName = pointsAdjustBroker.broker?.name || pointsAdjustBroker.broker_id
                        const opLabel = pointsAdjustOperation === 'add' ? 'adicionar' : 'retirar'
                        const qtyAbs = Math.abs(pointsAdjustDelta).toLocaleString('pt-BR')
                        if (
                          !window.confirm(
                            `Confirma ${opLabel} ${qtyAbs} PASI Points do corretor ${brokerName}?\n\nSaldo atual: ${pointsAdjustBroker.points_balance.toLocaleString('pt-BR')}\nSaldo resultante: ${pointsAdjustProjectedBalance.toLocaleString('pt-BR')}`
                          )
                        ) {
                          return
                        }
                        setSavingPointsAdjust(true)
                        const balanceBefore = pointsAdjustBroker.points_balance
                        const balanceAfter = balanceBefore + pointsAdjustDelta
                        try {
                          await shopService.adjustBrokerPoints({
                            shopBrokerId: pointsAdjustBroker.id,
                            pointsDelta: pointsAdjustDelta,
                            description: pointsAdjustObservation.trim(),
                            createdBy: user?.id ?? null,
                          })
                          try {
                            await userLogsService.logUpdate(
                              'other',
                              pointsAdjustBroker.id,
                              `Ajustou pontuação do corretor ${brokerName}: ${pointsAdjustDelta > 0 ? '+' : ''}${pointsAdjustDelta}`,
                              { points_balance: balanceBefore },
                              { points_balance: balanceAfter },
                              {
                                context: 'portais_shop',
                                shop_broker_id: pointsAdjustBroker.id,
                                broker_id: pointsAdjustBroker.broker_id,
                                broker_name: brokerName,
                                points_delta: pointsAdjustDelta,
                                observation: pointsAdjustObservation.trim(),
                                operation: pointsAdjustOperation,
                              }
                            )
                          } catch (logError) {
                            console.warn('Erro ao registrar log de ajuste de pontos:', logError)
                          }
                          await loadBrokers()
                          closePointsAdjustModal()
                        } catch (err: any) {
                          console.error('Erro ao ajustar pontos:', err)
                          alert(err?.message || 'Erro ao ajustar pontos. Tente novamente.')
                        } finally {
                          setSavingPointsAdjust(false)
                        }
                      }}
                    >
                      {savingPointsAdjust ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de Detalhes do Pedido */}
            {isOrderDetailModalOpen && selectedOrder && (
              <OrderDetailModal
                order={selectedOrder}
                          canEditOrder={canUpdateShop}
                onClose={() => {
                  setIsOrderDetailModalOpen(false)
                  setSelectedOrder(null)
                }}
                onUpdateStatus={async (updateData, options) => {
                  try {
                    setLoading(true)
                    const snapshotBefore = shopOrderSnapshotForActivityLog(selectedOrder)
                    await shopService.updateOrderStatus(selectedOrder.id, updateData)
                    if (!options?.skipLog) {
                      const snapshotAfter = mergedOrderSnapshotAfterUpdate(selectedOrder, updateData)
                      await userLogsService.logUpdate(
                        'other',
                        selectedOrder.id,
                        `Atualizou pedido no Shop: ${selectedOrder.order_number}`,
                        snapshotBefore,
                        snapshotAfter,
                        {
                          context: 'portais_shop',
                          order_number: selectedOrder.order_number,
                          broker_name: selectedOrder.broker?.name ?? null,
                        },
                      )
                    }
                    await loadOrders()
                    setIsOrderDetailModalOpen(false)
                    setSelectedOrder(null)
                  } catch (error) {
                    console.error('Erro ao atualizar pedido:', error)
                    alert('Erro ao atualizar pedido. Tente novamente.')
                  } finally {
                    setLoading(false)
                  }
                }}
              />
            )}
          </div>
        </PermissionGuard>
    </div>
  )
}

// Componente Modal de Produto
interface ProductModalProps {
  product: Partial<ShopProduct>
  categories: ShopCategory[]
  canInsert: boolean
  canUpdate: boolean
  onClose: () => void
  onSave: () => Promise<void>
}

const ProductModal: React.FC<ProductModalProps> = ({ product, categories, canInsert, canUpdate, onClose, onSave }) => {
  const isEditing = Boolean(product.id)
  const canManage = isEditing ? canUpdate : canInsert
  const [formData, setFormData] = useState({
    name: product.name || '',
    description: product.description || '',
    category_id: product.category_id || '',
    points_cost: product.points_cost || 0,
    is_active: product.is_active !== undefined ? product.is_active : true,
    is_featured: product.is_featured || false,
    is_new: product.is_new || false,
    requires_size: product.requires_size ?? false,
    available_sizes: product.available_sizes ?? [],
  })
  const [newSizeInput, setNewSizeInput] = useState('')
  const [selectedImages, setSelectedImages] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [existingImages, setExistingImages] = useState<ShopProductImage[]>([])
  const [primaryImageIndex, setPrimaryImageIndex] = useState<number | null>(null) // Para novas imagens
  const [primaryExistingImageId, setPrimaryExistingImageId] = useState<string | null>(null) // Para imagens existentes
  const [imageUrls, setImageUrls] = useState<Map<string, string>>(new Map()) // Cache de URLs assinadas
  
  // Carregar imagens existentes quando o produto for editado
  useEffect(() => {
    if (product.id) {
      shopService.listProductImages(product.id).then(async (images) => {
        setExistingImages(images)
        const primary = images.find(img => img.is_primary)
        if (primary) {
          setPrimaryExistingImageId(primary.id)
        }
        
        // Gerar URLs assinadas para todas as imagens
        const urlMap = new Map<string, string>()
        for (const img of images) {
          const signedUrl = await shopService.getProductImageUrl(img.image_url)
          if (signedUrl) {
            urlMap.set(img.id, signedUrl)
          }
        }
        setImageUrls(urlMap)
      }).catch(console.error)
    } else {
      setExistingImages([])
      setPrimaryExistingImageId(null)
      setImageUrls(new Map())
    }
  }, [product.id])

  // Atualizar formData quando product mudar
  useEffect(() => {
    setFormData({
      name: product.name || '',
      description: product.description || '',
      category_id: product.category_id || '',
      points_cost: product.points_cost || 0,
      is_active: product.is_active !== undefined ? product.is_active : true,
      is_featured: product.is_featured || false,
      is_new: product.is_new || false,
      requires_size: product.requires_size ?? false,
      available_sizes: product.available_sizes ?? [],
    })
    setNewSizeInput('')
    setSelectedImages([])
    setImagePreviews([])
    setPrimaryImageIndex(null)
  }, [product])
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)

  const addSize = () => {
    const trimmed = newSizeInput.trim()
    if (!trimmed) return
    const exists = formData.available_sizes.some(
      s => s.toLowerCase() === trimmed.toLowerCase()
    )
    if (exists) {
      setNewSizeInput('')
      return
    }
    setFormData(prev => ({
      ...prev,
      available_sizes: [...prev.available_sizes, trimmed],
    }))
    setNewSizeInput('')
  }

  const removeSize = (index: number) => {
    setFormData(prev => ({
      ...prev,
      available_sizes: prev.available_sizes.filter((_, i) => i !== index),
    }))
  }

  const handleSizeInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addSize()
    }
  }

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    const imageFiles = files.filter(file => file.type.startsWith('image/'))
    const currentLength = selectedImages.length
    
    // Criar previews primeiro
    const previewPromises = imageFiles.map(file => {
      return new Promise<string>((resolve) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          resolve(e.target?.result as string)
        }
        reader.readAsDataURL(file)
      })
    })

    Promise.all(previewPromises).then((previews) => {
      setSelectedImages(prev => [...prev, ...imageFiles])
      setImagePreviews(prev => [...prev, ...previews])
      
      // Se é a primeira imagem selecionada e não há imagens existentes com primary, marcar como principal
      if (currentLength === 0 && existingImages.length === 0 && primaryImageIndex === null) {
        setPrimaryImageIndex(0)
      }
    })
    
    // Limpar o input para permitir selecionar os mesmos arquivos novamente se necessário
    e.target.value = ''
  }

  const handleRemoveImage = (index: number) => {
    const newImages = selectedImages.filter((_, i) => i !== index)
    const newPreviews = imagePreviews.filter((_, i) => i !== index)
    setSelectedImages(newImages)
    setImagePreviews(newPreviews)
    
    // Se a imagem removida era a principal, definir a primeira como principal
    if (primaryImageIndex === index) {
      setPrimaryImageIndex(newImages.length > 0 ? 0 : null)
    } else if (primaryImageIndex !== null && primaryImageIndex > index) {
      // Ajustar índice se a imagem removida estava antes da principal
      setPrimaryImageIndex(primaryImageIndex - 1)
    }
  }

  const handleSetPrimaryExisting = async (imageId: string) => {
    try {
      if (!product.id) {
        throw new Error('ID do produto não encontrado')
      }

      // Primeiro, remover primary de todas as imagens existentes
      for (const img of existingImages) {
        if (img.is_primary && img.id !== imageId) {
          await shopService.upsertProductImage({
            id: img.id,
            is_primary: false
          })
        }
      }
      
      // Depois, definir a imagem selecionada como principal
      await shopService.upsertProductImage({
        id: imageId,
        is_primary: true
      })
      
      setPrimaryExistingImageId(imageId)
      
      // Recarregar imagens para atualizar o estado
      const images = await shopService.listProductImages(product.id)
      setExistingImages(images)
      
      // Atualizar URLs assinadas
      const urlMap = new Map<string, string>()
      for (const img of images) {
        const signedUrl = await shopService.getProductImageUrl(img.image_url)
        if (signedUrl) {
          urlMap.set(img.id, signedUrl)
        }
      }
      setImageUrls(urlMap)
    } catch (error) {
      console.error('Erro ao definir imagem principal:', error)
      alert('Erro ao definir imagem principal. Tente novamente.')
    }
  }

  const handleRemoveExistingImage = async (imageId: string, imageUrl: string) => {
    try {
      await shopService.deleteProductImage(imageId)
      await shopService.deleteProductImageFile(imageUrl)
      const newImages = existingImages.filter(img => img.id !== imageId)
      setExistingImages(newImages)
      
      // Se a imagem removida era a principal e ainda há imagens, definir a primeira como principal
      if (primaryExistingImageId === imageId && newImages.length > 0) {
        await handleSetPrimaryExisting(newImages[0].id)
      } else if (primaryExistingImageId === imageId) {
        setPrimaryExistingImageId(null)
      }
    } catch (error) {
      console.error('Erro ao remover imagem:', error)
      alert('Erro ao remover imagem. Tente novamente.')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canManage) return
    
    // Validação: não permitir salvar com 0 ou menos PASI Points
    if (formData.points_cost <= 0) {
      alert('O valor de PASI Points deve ser maior que zero.')
      return
    }

    if (formData.requires_size && formData.available_sizes.length === 0) {
      alert('Informe ao menos um tamanho quando o produto exige escolha de tamanho.')
      return
    }
    
    setSaving(true)

    try {
      // Salvar produto primeiro
      const productData: Partial<ShopProduct> = {
        ...formData,
        requires_size: formData.requires_size,
        available_sizes: formData.requires_size ? formData.available_sizes : [],
        id: product.id,
      }

      const savedProduct = await shopService.upsertProduct(productData)
      const productId = savedProduct?.id || product.id

      if (!productId) {
        throw new Error('ID do produto não encontrado')
      }

      // Upload de novas imagens
      if (selectedImages.length > 0) {
        setUploading(true)
        
        // Verificar se há imagem principal existente
        const hasExistingPrimary = existingImages.some(img => img.is_primary)
        
        // Determinar qual imagem será principal
        // Se não há imagens existentes, a primeira nova será principal (ou a marcada pelo usuário)
        // Se há imagens existentes com primary, só marcar nova como primary se o usuário explicitamente marcou
        const finalPrimaryIndex = primaryImageIndex !== null 
          ? primaryImageIndex 
          : (existingImages.length === 0 ? 0 : null)
        
        // Se uma nova imagem foi marcada como principal, remover primary das existentes primeiro
        if (finalPrimaryIndex !== null && hasExistingPrimary) {
          for (const img of existingImages) {
            if (img.is_primary) {
              await shopService.upsertProductImage({
                id: img.id,
                is_primary: false
              })
            }
          }
        }
        
        // Upload das novas imagens
        for (let i = 0; i < selectedImages.length; i++) {
          const imageUrl = await shopService.uploadProductImage(selectedImages[i], productId)
          const isPrimary = (finalPrimaryIndex !== null && i === finalPrimaryIndex) || 
                           (existingImages.length === 0 && i === 0 && finalPrimaryIndex === null)
          
          await shopService.upsertProductImage({
            product_id: productId,
            image_url: imageUrl,
            display_order: existingImages.length + i,
            is_primary: isPrimary,
          })
        }
        
        setUploading(false)
      }

      await onSave()
    } catch (error) {
      console.error('Erro ao salvar produto:', error)
      alert('Erro ao salvar produto. Tente novamente.')
    } finally {
      setSaving(false)
      setUploading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  {product.id ? 'Editar Produto' : 'Novo Produto'}
                </Heading>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <span className="sr-only">Fechar</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {!canManage && (
                <Text size="sm" className="mb-4 text-amber-700 dark:text-amber-300">
                  {isEditing
                    ? 'Você não possui permissão de edição para salvar alterações neste produto.'
                    : 'Você não possui permissão de inserção para criar este produto.'}
                </Text>
              )}
              <fieldset disabled={!canManage} className="space-y-4 disabled:opacity-80">
                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Nome *</Text>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full"
                  />
                </div>

                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Descrição</Text>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Categoria *</Text>
                    <select
                      value={formData.category_id}
                      onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                      required
                      className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Selecione...</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">PASI Points *</Text>
                    <Input
                      type="number"
                      min="1"
                      value={formData.points_cost}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0
                        setFormData({ ...formData, points_cost: value > 0 ? value : 1 })
                      }}
                      required
                      className="w-full"
                    />
                    {formData.points_cost <= 0 && (
                      <Text size="xs" className="text-red-500 dark:text-red-400 mt-1">
                        O valor de PASI Points deve ser maior que zero.
                      </Text>
                    )}
                  </div>
                </div>

                {/* Estoque: somente leitura (não editável) */}
                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1 block">Quantidade em estoque</Text>
                  <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                    {product.id != null && typeof product.stock_quantity === 'number'
                      ? product.stock_quantity
                      : '—'}
                  </Text>
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <Text size="sm" className="ml-2 text-gray-700 dark:text-gray-300">Ativo</Text>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <Text size="sm" className="ml-2 text-gray-700 dark:text-gray-300">Em Destaque</Text>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_new}
                      onChange={(e) => setFormData({ ...formData, is_new: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <Text size="sm" className="ml-2 text-gray-700 dark:text-gray-300">Novo</Text>
                  </label>
                </div>

                <div>
                  <label className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      checked={formData.requires_size}
                      onChange={(e) => setFormData({
                        ...formData,
                        requires_size: e.target.checked,
                        available_sizes: e.target.checked ? formData.available_sizes : [],
                      })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <Text size="sm" className="ml-2 text-gray-700 dark:text-gray-300">Exige tamanho</Text>
                  </label>
                  {formData.requires_size && (
                    <div className="mt-2 space-y-2">
                      <Text size="xs" color="muted">
                        Tamanhos oferecidos (ex.: P, M, G). O corretor deverá escolher um ao adicionar ao carrinho.
                      </Text>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          value={newSizeInput}
                          onChange={(e) => setNewSizeInput(e.target.value)}
                          onKeyDown={handleSizeInputKeyDown}
                          placeholder="Digite um tamanho..."
                          className="flex-1"
                        />
                        <Button type="button" variant="outline" onClick={addSize}>
                          Adicionar
                        </Button>
                      </div>
                      {formData.available_sizes.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {formData.available_sizes.map((size, index) => (
                            <span
                              key={`${size}-${index}`}
                              className="inline-flex items-center gap-1 px-2 py-1 text-sm rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                            >
                              {size}
                              <button
                                type="button"
                                onClick={() => removeSize(index)}
                                className="text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                                title="Remover tamanho"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <Text size="xs" className="text-red-500 dark:text-red-400">
                          Adicione ao menos um tamanho.
                        </Text>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Imagens</Text>
                  
                  {/* Imagens existentes */}
                  {existingImages.length > 0 && (
                    <div className="mb-4">
                      <Text size="xs" color="muted" className="mb-2 block">Imagens Existentes</Text>
                      <div className="grid grid-cols-4 gap-4">
                        {existingImages.map((img) => {
                          const displayUrl = imageUrls.get(img.id) || img.image_url
                          return (
                            <div key={img.id} className="relative group">
                              <img
                                src={displayUrl}
                                alt={img.alt_text || 'Produto'}
                                className="w-full h-24 object-cover rounded border-2 border-transparent"
                                style={{
                                  borderColor: img.is_primary ? '#008B95' : 'transparent'
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveExistingImage(img.id, img.image_url)}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Remover imagem"
                              >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSetPrimaryExisting(img.id)}
                                className={`absolute bottom-1 left-1 text-xs px-2 py-1 rounded ${
                                  img.is_primary
                                    ? 'bg-primary-500 text-white'
                                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity'
                                }`}
                                title={img.is_primary ? 'Imagem Principal' : 'Definir como Principal'}
                              >
                                {img.is_primary ? 'Principal' : 'Principal'}
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* Preview de novas imagens */}
                  {imagePreviews.length > 0 && (
                    <div className="mb-4">
                      <Text size="xs" color="muted" className="mb-2 block">Novas Imagens</Text>
                      <div className="grid grid-cols-4 gap-4">
                        {imagePreviews.map((preview, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={preview}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-24 object-cover rounded border-2 border-transparent"
                              style={{
                                borderColor: primaryImageIndex === index ? '#008B95' : 'transparent'
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Remover imagem"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => setPrimaryImageIndex(index)}
                              className={`absolute bottom-1 left-1 text-xs px-2 py-1 rounded ${
                                primaryImageIndex === index
                                  ? 'bg-primary-500 text-white'
                                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity'
                              }`}
                              title={primaryImageIndex === index ? 'Imagem Principal' : 'Definir como Principal'}
                            >
                              {primaryImageIndex === index ? 'Principal' : 'Principal'}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                    className="block w-full text-sm text-gray-700 dark:text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 dark:file:bg-gray-700 dark:file:text-gray-200"
                  />
                </div>
              </fieldset>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              {canManage && (
                <Button
                  type="submit"
                  variant="solid"
                  disabled={saving || uploading}
                  className="w-full sm:ml-3 sm:w-auto"
                >
                  {saving || uploading ? 'Salvando...' : 'Salvar'}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="mt-3 w-full sm:mt-0 sm:w-auto"
              >
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

// Componente Modal de Categoria
interface CategoryModalProps {
  category: Partial<ShopCategory>
  onClose: () => void
  onSave: () => Promise<void>
}

const CategoryModal: React.FC<CategoryModalProps> = ({ category, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: category.name || '',
    description: category.description || '',
    slug: category.slug || '',
    is_active: category.is_active !== undefined ? category.is_active : true,
    display_order: category.display_order || 0,
  })
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)

    try {
      // Gerar slug automaticamente se não fornecido
      const slug = formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      const categoryData: Partial<ShopCategory> = { ...formData, slug, id: category.id }
      await shopService.upsertCategory(categoryData)
      await onSave()
    } catch (error) {
      console.error('Erro ao salvar categoria:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  {category.id ? 'Editar Categoria' : 'Nova Categoria'}
                </Heading>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <span className="sr-only">Fechar</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Nome *</Text>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full"
                  />
                </div>

                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Descrição</Text>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Slug</Text>
                    <Input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      placeholder="gerado-automaticamente"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Ordem de Exibição</Text>
                    <Input
                      type="number"
                      min="0"
                      value={formData.display_order}
                      onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                      className="w-full"
                    />
                  </div>
                </div>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <Text size="sm" className="ml-2 text-gray-700 dark:text-gray-300">Ativa</Text>
                </label>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <Button
                type="submit"
                variant="solid"
                disabled={saving}
                className="w-full sm:ml-3 sm:w-auto"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="mt-3 w-full sm:mt-0 sm:w-auto"
              >
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

// Componente Modal de Detalhes do Pedido
interface OrderDetailModalProps {
  order: ShopOrder
  canEditOrder: boolean
  onClose: () => void
  onUpdateStatus: (data: ShopOrderUpdateData, options?: { skipLog?: boolean }) => Promise<void>
}

function formatShopRecipientCpf(cpf: string | null | undefined): string {
  if (!cpf?.trim()) return ''
  const cleaned = cpf.replace(/\D/g, '')
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }
  return cpf.trim()
}

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ order, canEditOrder, onClose, onUpdateStatus }) => {
  const { profile } = useAuthContext()
  const [updateData, setUpdateData] = useState<ShopOrderUpdateData>({
    status: order.status,
    shipping_tracking_code: order.shipping_tracking_code || '',
    shipping_provider: order.shipping_provider || '',
    shipping_cost: order.shipping_cost ?? null,
    notes: order.notes || '',
  })
  const [saving, setSaving] = useState(false)
  const [showSaveConfirm, setShowSaveConfirm] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelConfirmChecked, setCancelConfirmChecked] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [cancelledByName, setCancelledByName] = useState<string | null>(null)
  const recipientCpfDisplay = formatShopRecipientCpf(order.recipient_cpf)

  useEffect(() => {
    if (order.status !== 'cancelled' || !order.cancelled_by) {
      setCancelledByName(null)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const { data } = await supabase
          .from('profiles')
          .select('name')
          .eq('id', order.cancelled_by!)
          .single()
        if (!cancelled) setCancelledByName((data as { name?: string } | null)?.name ?? null)
      } catch {
        if (!cancelled) setCancelledByName(null)
      }
    })()
    return () => { cancelled = true }
  }, [order.status, order.cancelled_by])

  useEffect(() => {
    setUpdateData({
      status: order.status,
      shipping_tracking_code: order.shipping_tracking_code || '',
      shipping_provider: order.shipping_provider || '',
      shipping_cost: order.shipping_cost ?? null,
      notes: order.notes || '',
    })
  }, [
    order.id,
    order.status,
    order.shipping_tracking_code,
    order.shipping_provider,
    order.shipping_cost,
    order.notes,
  ])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canEditOrder) return
    setShowSaveConfirm(true)
  }

  const handleConfirmSave = async () => {
    if (!canEditOrder) return
    setSaving(true)
    try {
      await onUpdateStatus({
        ...updateData,
        shipping_cost:
          updateData.shipping_cost === null ||
          updateData.shipping_cost === undefined ||
          Number.isNaN(Number(updateData.shipping_cost))
            ? null
            : Number(updateData.shipping_cost),
      })
      setShowSaveConfirm(false)
    } catch (error) {
      console.error('Erro ao atualizar pedido:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleConfirmCancel = async () => {
    if (!canEditOrder) return
    const reason = cancelReason.trim()
    if (!reason || !cancelConfirmChecked) return
    setCancelling(true)
    try {
      const snapshotBefore = shopOrderSnapshotForActivityLog(order)
      await shopService.cancelOrder(order.id, profile?.id ?? null, reason)
      const snapshotAfterCancelled: Record<string, unknown> = {
        ...snapshotBefore,
        status: 'cancelled',
        cancellation_reason: reason,
      }
      await userLogsService.logUpdate(
        'other',
        order.id,
        `Cancelou pedido no Shop: ${order.order_number}`,
        snapshotBefore,
        snapshotAfterCancelled,
        {
          context: 'portais_shop',
          order_number: order.order_number,
          broker_name: order.broker?.name ?? null,
          action_detail: 'cancel_order',
        },
      )
      setShowCancelConfirm(false)
      setCancelReason('')
      setCancelConfirmChecked(false)
      await onUpdateStatus({ status: 'cancelled' }, { skipLog: true })
    } catch (error) {
      console.error('Erro ao cancelar pedido:', error)
      alert('Erro ao cancelar pedido. Tente novamente.')
    } finally {
      setCancelling(false)
    }
  }

  const formatDateTime = (iso: string | null) => {
    if (!iso) return '-'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '-'
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  }

  return (
    <>
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Pedido {order.order_number}
                </Heading>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <span className="sr-only">Fechar</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Informações do Pedido */}
                <div>
                  <Heading as="h4" size="2xl" className="text-gray-900 dark:text-white mb-3">Informações do Pedido</Heading>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Text size="sm" className="text-gray-500 dark:text-gray-400">Corretor</Text>
                      <Text size="sm" className="text-gray-900 dark:text-white">{order.broker?.name}</Text>
                    </div>
                    <div>
                      <Text size="sm" className="text-gray-500 dark:text-gray-400">Total de PASI Points</Text>
                      <Text size="sm" className="text-gray-900 dark:text-white font-bold">{order.total_points.toLocaleString('pt-BR')}</Text>
                    </div>
                    <div>
                      <Text size="sm" className="text-gray-500 dark:text-gray-400">Data do Pedido</Text>
                      <Text size="sm" className="text-gray-900 dark:text-white">{formatDateTime(order.created_at)}</Text>
                    </div>
                    <div>
                      <Text size="sm" className="text-gray-500 dark:text-gray-400">Status Atual</Text>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                        order.status === 'confirmed' ? 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200' :
                        order.status === 'shipped' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                        order.status === 'delivered' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {order.status === 'pending' ? 'Pendente' :
                         order.status === 'confirmed' ? 'Confirmado' :
                         order.status === 'shipped' ? 'Enviado' :
                         order.status === 'delivered' ? 'Entregue' :
                         'Cancelado'}
                      </span>
                    </div>
                    {order.status === 'cancelled' && (
                      <>
                        {order.cancellation_reason && (
                          <div className="col-span-2">
                            <Text size="sm" className="text-gray-500 dark:text-gray-400">Motivo do cancelamento</Text>
                            <Text size="sm" className="text-gray-900 dark:text-white">{order.cancellation_reason}</Text>
                          </div>
                        )}
                        {(order.cancelled_by || cancelledByName) && (
                          <div className="col-span-2">
                            <Text size="sm" className="text-gray-500 dark:text-gray-400">Cancelado por</Text>
                            <Text size="sm" className="text-gray-900 dark:text-white">{cancelledByName ?? order.cancelled_by ?? '—'}</Text>
                          </div>
                        )}
                        {order.cancelled_at && (
                          <div>
                            <Text size="sm" className="text-gray-500 dark:text-gray-400">Data do cancelamento</Text>
                            <Text size="sm" className="text-gray-900 dark:text-white">{formatDateTime(order.cancelled_at)}</Text>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Itens do Pedido */}
                {order.items && order.items.length > 0 && (
                  <div>
                    <Heading as="h4" size="2xl" className="text-gray-900 dark:text-white mb-3">Itens do Pedido</Heading>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Produto</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tamanho</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantidade</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">PASI Points Unit.</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {order.items.map((item) => (
                            <tr key={item.id}>
                              <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{item.product_name}</td>
                              <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{item.size_label ?? '—'}</td>
                              <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{item.quantity}</td>
                              <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{item.points_cost.toLocaleString('pt-BR')}</td>
                              <td className="px-4 py-2 text-sm text-gray-900 dark:text-white font-bold">{(item.quantity * item.points_cost).toLocaleString('pt-BR')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Endereço de Entrega */}
                <div>
                  <Heading as="h4" size="2xl" className="text-gray-900 dark:text-white mb-3">Endereço de Entrega</Heading>
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded">
                    <Text size="sm" className="text-gray-900 dark:text-white">
                      {order.recipient_name}<br />
                      {recipientCpfDisplay && (
                        <>
                          CPF: {recipientCpfDisplay}
                          <br />
                        </>
                      )}
                      {order.street}, {order.number}{order.complement ? ` - ${order.complement}` : ''}<br />
                      {order.neighborhood} - {order.city}/{order.state}<br />
                      CEP: {order.zip_code}
                      {order.reference_point && <><br />Referência: {order.reference_point}</>}
                    </Text>
                  </div>
                </div>

                {/* Atualização de Status - oculto quando pedido cancelado */}
                {order.status !== 'cancelled' && canEditOrder && (
                  <div>
                    <Heading as="h4" size="2xl" className="text-gray-900 dark:text-white mb-3">Atualizar Status</Heading>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Status *</Text>
                          <select
                            value={updateData.status}
                            onChange={(e) =>
                              setUpdateData({ ...updateData, status: e.target.value as ShopOrderStatus })
                            }
                            required
                            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                          >
                            <option value="pending">Pendente</option>
                            <option value="confirmed">Confirmado</option>
                            <option value="shipped">Enviado</option>
                            <option value="delivered">Entregue</option>
                          </select>
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Valor frete (R$)</Text>
                          <Input
                            type="number"
                            min={0}
                            step="0.01"
                            placeholder="0,00"
                            value={
                              updateData.shipping_cost === null || updateData.shipping_cost === undefined
                                ? ''
                                : updateData.shipping_cost
                            }
                            onChange={(e) => {
                              const v = e.target.value
                              if (v === '') {
                                setUpdateData({ ...updateData, shipping_cost: null })
                                return
                              }
                              const n = parseFloat(v)
                              setUpdateData({
                                ...updateData,
                                shipping_cost: Number.isNaN(n) ? null : n,
                              })
                            }}
                            className="w-full"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Código de Rastreamento</Text>
                          <Input
                            type="text"
                            value={updateData.shipping_tracking_code || ''}
                            onChange={(e) => setUpdateData({ ...updateData, shipping_tracking_code: e.target.value })}
                            className="w-full"
                          />
                        </div>

                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Transportadora</Text>
                          <Input
                            type="text"
                            value={updateData.shipping_provider || ''}
                            onChange={(e) => setUpdateData({ ...updateData, shipping_provider: e.target.value })}
                            className="w-full"
                          />
                        </div>
                      </div>

                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Observações</Text>
                        <textarea
                          value={updateData.notes || ''}
                          onChange={(e) => setUpdateData({ ...updateData, notes: e.target.value })}
                          rows={3}
                          className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                {order.status !== 'cancelled' && canEditOrder && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCancelConfirm(true)}
                    className="w-full sm:w-auto border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
                  >
                    Cancelar pedido
                  </Button>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end">
                <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
                  Fechar
                </Button>
                {order.status !== 'cancelled' && canEditOrder && (
                  <Button
                    type="submit"
                    variant="solid"
                    disabled={saving}
                    className="w-full sm:w-auto"
                  >
                    {saving ? 'Salvando...' : 'Salvar Alterações'}
                  </Button>
                )}
              </div>
            </div>
          </form>

          {/* Modal de confirmação de cancelamento (dupla confirmação) */}
          {showCancelConfirm && (
            <div className="fixed inset-0 z-[60] overflow-y-auto">
              <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => !cancelling && setShowCancelConfirm(false)} />
                <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                  <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                    <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-3">
                      Confirmar cancelamento
                    </Heading>
                    <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 mb-4">
                      <Text size="sm" className="text-amber-800 dark:text-amber-200">
                        Esta ação não poderá ser desfeita. O pedido será cancelado, o estoque será devolvido e os PASI Points do corretor serão revertidos.
                      </Text>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Motivo do cancelamento (obrigatório)
                      </label>
                      <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 p-4 mb-4">
                        <Text size="sm" className="text-red-800 dark:text-red-200 font-bold">
                          Esta informação será exibida para o corretor.
                        </Text>
                      </div>
                      <textarea
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="Informe o motivo do cancelamento (obrigatório)"
                        rows={3}
                        className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                        disabled={cancelling}
                      />
                    </div>
                    <label className="flex items-start gap-2 mb-4 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cancelConfirmChecked}
                        onChange={(e) => setCancelConfirmChecked(e.target.checked)}
                        disabled={cancelling}
                        className="mt-1 rounded border-gray-300 dark:border-gray-600"
                      />
                      <Text size="sm" className="text-gray-700 dark:text-gray-300">
                        Confirmo que desejo cancelar este pedido e que esta ação é irreversível.
                      </Text>
                    </label>
                    <div className="flex flex-row-reverse gap-2 sm:gap-3">
                      <Button
                        type="button"
                        variant="solid"
                        onClick={handleConfirmCancel}
                        disabled={cancelling || !cancelReason.trim() || !cancelConfirmChecked}
                        className="border-red-600 bg-red-600 hover:bg-red-700 text-white"
                      >
                        {cancelling ? 'Cancelando...' : 'Confirmar cancelamento'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => { setShowCancelConfirm(false); setCancelReason(''); setCancelConfirmChecked(false) }}
                        disabled={cancelling}
                      >
                        Voltar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Modal de confirmação ao salvar alterações — fora do painel com transform para centralizar na tela */}
    {showSaveConfirm && (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-500/75 p-4"
        onClick={() => !saving && setShowSaveConfirm(false)}
      >
        <div
          className="relative w-full max-w-lg rounded-lg bg-white dark:bg-gray-800 shadow-xl overflow-hidden text-left"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-4 pt-5 pb-4 sm:p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-3">
              Confirmar alterações
            </Heading>
            <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-4">
              Deseja salvar as alterações do pedido {order.order_number}?
            </Text>
            <div className="flex flex-row-reverse gap-2 sm:gap-3">
              <Button
                type="button"
                variant="solid"
                onClick={handleConfirmSave}
                disabled={saving}
              >
                {saving ? 'Salvando...' : 'Confirmar'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowSaveConfirm(false)}
                disabled={saving}
              >
                Voltar
              </Button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  )
}

export default Shop

